<template>
  <!-- Liste des demandes -->
  <template v-if="!adminRefsShootingId">
    <!-- Page Header -->
    <PageHeader
      title="Liste de vos demandes de mises en ligne"
      subtitle="Gérez et suivez vos demandes de shooting photo"
      class="mb-8"
    />

    <div class="w-full px-0 py-0 md:px-6 md:py-2">
      <!-- Filtres compacts -->
      <FiltersCompact
        v-model="filters"
        :is-loading="isFiltering"
        @filter="handleFilter"
      />

      <!-- Légende des types de shooting -->
      <div class="mb-6 bg-white p-4 rounded-lg border border-gray-200">
        <div class="flex flex-col gap-3">
          <div class="flex items-center gap-6">
            <span class="text-sm font-medium text-black">Statut :</span>
            <div class="flex items-center gap-2">
              <div class="w-2 h-6 bg-green-400 rounded"></div>
              <span class="text-sm text-black">Confirmé</span>
            </div>
            <div class="flex items-center gap-2">
              <div class="w-2 h-6 bg-yellow-400 rounded"></div>
              <span class="text-sm text-black">En attente</span>
            </div>
          </div>
          <div class="flex items-center gap-6">
            <span class="text-sm font-medium text-black"
              >Type de shooting :</span
            >
            <div class="flex items-center gap-2">
              <div class="w-4 h-4 bg-green-500 rounded-full"></div>
              <span class="text-sm text-black">Shooting Couleurs</span>
            </div>
            <div class="flex items-center gap-2">
              <div class="w-4 h-4 bg-gray-400 rounded-full"></div>
              <span class="text-sm text-black">Shooting Uni</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Loader -->
      <div
        v-if="isLoading"
        class="bg-white rounded-lg border border-gray-200 shadow-sm p-12"
      >
        <div class="flex flex-col items-center justify-center">
          <div
            class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"
          ></div>
          <p class="text-black text-sm">Chargement des demandes...</p>
        </div>
      </div>

      <!-- Tableau des demandes -->
      <div v-else class="bg-white rounded-lg border border-gray-200 shadow-sm">
        <ReusableTable
          :columns="tableColumns"
          :data="demandes"
          :row-class="getRowClass"
          :show-edit="true"
          :show-delete="true"
        >
          <!-- ID + Badge € pour shooting Uni/Couleurs -->
          <template #column-id="{ row }">
            <div class="flex items-center gap-2">
              <span class="font-semibold text-black">{{ row.id }}</span>

              <!-- Badge € pour shooting Couleurs (4, 6) -->
              <span
                v-if="row.prestationValeur === 4 || row.prestationValeur === 6"
                class="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold bg-green-500 text-white rounded"
                title="Shooting Couleurs"
              >
                <i class="fas fa-euro-sign"></i>
              </span>

              <!-- Badge € pour shooting Uni (3, 5) -->
              <span
                v-if="row.prestationValeur === 3 || row.prestationValeur === 5"
                class="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold bg-gray-400 text-white rounded"
                title="Shooting Uni"
              >
                <i class="fas fa-euro-sign"></i>
              </span>
            </div>
          </template>

          <!-- Type Shooting - Badge MEL + Prestation -->
          <template #column-typeShooting="{ row }">
            <div class="flex flex-col gap-2">
              <!-- Badge MEL (UPGR, TGR, eFashion) -->
              <span
                :class="[
                  'inline-block px-2 py-1 text-xs font-semibold rounded',
                  row.mel === '1' || row.mel === 1
                    ? 'bg-gray-300 text-black'
                    : '',
                  row.mel === '2' || row.mel === 2
                    ? 'bg-yellow-400 text-black'
                    : '',
                  row.mel === '0' || row.mel === 0
                    ? 'bg-blue-500 text-white'
                    : '',
                ]"
              >
                {{ row.melType }}
              </span>

              <!-- Type de prestation (affiché pour eFashion uniquement, car UPGR/TGR n'ont pas de prestation) -->
              <span
                v-if="row.mel === '0' || row.mel === 0"
                class="text-xs text-black leading-tight"
              >
                {{ row.prestationType }}
              </span>
            </div>
          </template>

          <template #actions="{ row }">
            <div class="flex items-center gap-1">
              <!-- Modifier / Voir - vrai lien pour clic droit « Ouvrir dans un nouvel onglet » -->
              <a
                :href="getEditShootingHref(Number(row.id))"
                @click.prevent="handleEdit(Number(row.id))"
                :class="[
                  'w-7 h-7 flex items-center justify-center text-xs rounded transition-colors',
                  !authStore.isImpersonating &&
                  (row.isProgrammed || row.isConfirmed)
                    ? 'bg-gray-500 text-white hover:bg-gray-600'
                    : 'bg-blue-500 text-white hover:bg-blue-600',
                ]"
                :title="
                  authStore.isImpersonating
                    ? 'Modifier'
                    : row.isProgrammed
                      ? 'Voir (shooting programmé - modification impossible)'
                      : row.isConfirmed
                        ? 'Voir (shooting confirmé - modification impossible)'
                        : 'Modifier'
                "
              >
                <i class="fas fa-edit"></i>
              </a>

              <!-- Uploads - UPGR sans prestation (vendeur) -->
              <a
                v-if="
                  (row.mel === '1' || row.mel === 1) &&
                  row.prestationValeur === null
                "
                :href="getUploadShootingHref(Number(row.id))"
                @click.prevent="handleUpload(Number(row.id))"
                class="w-7 h-7 flex items-center justify-center text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                title="Upload photos (UPGR)"
              >
                <i class="fas fa-upload"></i>
              </a>

              <!-- Upload admin (impersonation EFP) — vrai lien pour clic droit nouvel onglet -->
              <a
                v-if="authStore.isImpersonating"
                :href="getAdminRefsHref(Number(row.id))"
                @click.prevent="openAdminRefsFocus(Number(row.id))"
                class="w-7 h-7 flex items-center justify-center text-xs bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                title="Références du shooting (admin)"
              >
                <i class="fas fa-upload"></i>
              </a>

              <!-- Photos - Visible si droits de téléchargement -->
              <button
                v-if="canDownloadPhotos(row)"
                @click="handlePhotos(Number(row.id))"
                class="w-7 h-7 flex items-center justify-center text-xs bg-cyan-500 text-white rounded hover:bg-cyan-600 transition-colors"
                title="Photos"
              >
                <i class="fas fa-camera"></i>
              </button>

              <!-- Étiquette de préparation - visible une fois la date du shooting confirmée -->
              <button
                v-if="
                  row.hasShootingDate &&
                  row.isConfirmed &&
                  row.mel !== '1' &&
                  row.mel !== 1
                "
                type="button"
                class="w-7 h-7 flex items-center justify-center text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                title="Imprimer l’étiquette du shooting"
                @click="openPickupLabelModal(Number(row.id))"
              >
                <i class="fas fa-qrcode" aria-hidden="true"></i>
              </button>

              <!-- Dupliquer un produit du shooting -->
              <button
                v-if="authStore.isImpersonating"
                @click="openDuplicateFromShooting(Number(row.id))"
                class="w-7 h-7 flex items-center justify-center text-xs bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                title="Dupliquer un produit"
              >
                <i class="fas fa-clone"></i>
              </button>

              <!-- Confirmer la date (admin uniquement) — shooting non encore confirmé -->
              <button
                v-if="authStore.isImpersonating && canConfirmerShooting(row)"
                type="button"
                @click="openConfirmerModal(Number(row.id))"
                class="w-7 h-7 flex items-center justify-center text-xs bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors"
                title="Confirmer le shooting (choisir la date)"
              >
                <i class="fas fa-calendar-check"></i>
              </button>

              <!-- Finaliser (admin uniquement) — génère la facture fournisseur -->
              <button
                v-if="authStore.isImpersonating && canFinaliserShooting(row)"
                :disabled="
                  finalisingShootingId === Number(row.id) ||
                  !row.allProductsHavePhotos
                "
                @click="handleFinaliser(Number(row.id), '1')"
                class="w-7 h-7 flex items-center justify-center text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :title="
                  !row.allProductsHavePhotos
                    ? 'Finaliser le shooting — tous les produits doivent avoir au moins une photo'
                    : 'Finaliser le shooting (ajouter à la facture)'
                "
              >
                <i class="fas fa-check"></i>
              </button>

              <!-- Annuler la finalisation (admin uniquement) -->
              <button
                v-if="authStore.isImpersonating && canAnnulerFinalisation(row)"
                :disabled="finalisingShootingId === Number(row.id)"
                @click="handleFinaliser(Number(row.id), '0')"
                class="w-7 h-7 flex items-center justify-center text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Annuler la finalisation"
              >
                <i class="fas fa-rotate-left"></i>
              </button>
            </div>
          </template>
        </ReusableTable>
      </div>

      <!-- Pagination -->
      <UnifiedPagination
        :total-items="totalItems"
        :current-page="currentPage"
        :items-per-page="itemsPerPage"
        variant="product"
        item-label="uploads"
        :items-per-page-options="[10, 25, 50, 100]"
        :show-first-last-buttons="true"
        :max-visible-pages="5"
        @update:currentPage="handlePageChange"
        @update:itemsPerPage="handleItemsPerPageChange"
        @page-change="handlePageChange"
        @items-per-page-change="handleItemsPerPageChange"
      />
    </div>

    <ModalComp
      v-model:isVisible="pickupLabelModalVisible"
      size="md"
      title="Type de réception"
    >
      <template #body>
        <div
          v-if="isLoadingPickupLabelConfiguration"
          class="flex items-center justify-center py-10 text-sm text-gray-600"
        >
          <i class="fas fa-spinner fa-spin mr-2"></i>
          Chargement...
        </div>
        <div v-else class="space-y-4">
          <div
            v-for="type in pickupPackageTypes"
            :key="type.id"
            class="flex items-center justify-between gap-4"
          >
            <label class="flex cursor-pointer items-center gap-3 text-base text-black">
              <input
                v-model="selectedPickupPackages[type.id]"
                type="checkbox"
                class="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {{ type.label }}
            </label>
            <input
              v-model.number="pickupPackageQuantities[type.id]"
              type="number"
              min="1"
              max="20"
              step="1"
              :disabled="!selectedPickupPackages[type.id]"
              class="w-24 rounded-lg border border-gray-300 px-3 py-2 text-center text-base text-black disabled:bg-gray-100 disabled:text-gray-400"
            />
          </div>

          <div class="border-t border-gray-200 pt-4 space-y-4">
            <label
              v-for="type in pickupHangerTypes"
              :key="type.id"
              class="flex cursor-pointer items-center gap-3 text-base text-black"
            >
              <input
                type="checkbox"
                :checked="pickupHangerTypeId === type.id"
                class="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                @change="pickupHangerTypeId = pickupHangerTypeId === type.id ? null : type.id"
              />
              {{ type.label }}
            </label>
          </div>

          <p
            v-if="pickupLabelCount > 0"
            class="rounded-md bg-blue-50 px-3 py-2 text-sm font-medium text-blue-800"
          >
            {{ pickupLabelCount }} étiquette{{ pickupLabelCount > 1 ? "s" : "" }}
            {{ pickupLabelCount > 1 ? "seront" : "sera" }} générée{{
              pickupLabelCount > 1 ? "s" : ""
            }}.
          </p>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end gap-3">
          <button
            type="button"
            class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-black hover:bg-gray-50"
            :disabled="isSavingPickupLabelConfiguration"
            @click="pickupLabelModalVisible = false"
          >
            Annuler
          </button>
          <button
            type="button"
            class="rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="
              isSavingPickupLabelConfiguration ||
              pickupLabelCount < 1 ||
              pickupHangerTypeId === null
            "
            @click="saveAndOpenPickupLabels"
          >
            <i
              v-if="isSavingPickupLabelConfiguration"
              class="fas fa-spinner fa-spin mr-2"
            ></i>
            Sauvegarder et imprimer
          </button>
        </div>
      </template>
    </ModalComp>
  </template>

  <!-- Vue admin : références du shooting uniquement -->
  <div v-else class="w-full px-6 pb-4 pt-24 max-md:pt-28">
    <nav
      class="mb-6 flex flex-wrap items-center gap-2 text-sm text-black"
      aria-label="Fil d'Ariane"
    >
      <button
        type="button"
        class="text-blue-600 hover:text-blue-800 hover:underline font-medium"
        @click="closeAdminRefsFocus"
      >
        Liste des mises en ligne
      </button>
      <span class="text-black" aria-hidden="true">/</span>
      <span class="text-black font-semibold">
        Demande #{{ adminRefsShootingId }}
      </span>
    </nav>

    <div
      v-if="adminRefsLoading"
      class="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center"
    >
      <div
        class="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"
      ></div>
      <p class="text-black text-sm">Chargement des références…</p>
    </div>

    <div
      v-else
      class="admin-mel-refs-root bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
    >
      <div class="px-4 py-3 border-b border-gray-200">
        <h2 class="text-base font-semibold text-black">
          Références
          <span class="text-black font-normal text-sm">
            ({{ adminRefsTotal }})
          </span>
        </h2>
      </div>

      <div
        v-if="adminRefsTotal > 0 || !!adminRefsReferenceFilter.trim()"
        class="px-4 pt-3 pb-3 border-b border-gray-200 bg-gray-50/90 space-y-3"
      >
        <div class="w-full min-w-0">
          <label class="sr-only" for="admin-refs-ref-filter"
            >Filtrer par référence</label
          >
          <input
            id="admin-refs-ref-filter"
            v-model.trim="adminRefsReferenceFilter"
            type="search"
            autocomplete="off"
            class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-black shadow-sm placeholder:text-black focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400"
            placeholder="Filtrer par référence…"
          />
        </div>
        <input
          ref="adminBulkNamedPhotosInputRef"
          type="file"
          multiple
          :accept="productPhotoInputAccept"
          class="hidden"
          @change="onAdminBulkNamedPhotosInputChange"
        />
        <div
          role="button"
          tabindex="0"
          :class="[
            'relative rounded-lg border-2 border-dashed px-4 py-4 transition-colors text-left outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2',
            isAdminBulkNamedUploading
              ? 'border-gray-200 bg-gray-100 cursor-wait'
              : isAdminBulkNamedDragOver
              ? 'border-orange-500 bg-orange-50 cursor-copy'
              : 'border-orange-200 bg-white hover:border-orange-400 hover:bg-orange-50/50 cursor-pointer',
          ]"
          @click="onAdminBulkNamedDropzoneClick"
          @keydown.enter.prevent="onAdminBulkNamedDropzoneClick"
          @keydown.space.prevent="onAdminBulkNamedDropzoneClick"
          @dragenter.prevent="onAdminBulkNamedDragEnter"
          @dragover.prevent="onAdminBulkNamedDragOver"
          @dragleave.prevent="onAdminBulkNamedDragLeave"
          @drop.prevent="onAdminBulkNamedDrop"
        >
          <!-- pointer-events-none : le drag cible sinon les <p>/<code> sans preventDefault → drop refusé -->
          <div
            class="pointer-events-none flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
          >
            <div class="min-w-0 flex-1 space-y-2">
              <p class="text-sm font-semibold text-black leading-snug">
                Photos en masse
              </p>
              <p
                class="text-sm text-black leading-relaxed break-words [overflow-wrap:anywhere] whitespace-normal"
              >
                Déposez une ou plusieurs images :
                <br />
                - Nom conseillé : <strong>{référence}-{position}.ext</strong>
                (position <strong>0</strong> à <strong>19</strong>, ex.
                <code class="rounded bg-gray-100 px-1 py-0.5 text-[13px]"
                  >MON-SKU-ROUGE-0.jpg</code
                >
                pour la couverture,
                <code class="rounded bg-gray-100 px-1 py-0.5 text-[13px]"
                  >MON-SKU-ROUGE-1.jpg</code
                >
                pour la 2ᵉ photo).
                <br />
                - En admin, un nombre final supérieur à
                <strong>19</strong> est accepté (ex.
                <code class="rounded bg-gray-100 px-1 py-0.5 text-[13px]"
                  >REF-robe-50.jpg</code
                >).
                <br />
                - Si besoin, on essaie aussi avec la partie avant le premier
                tiret (ex.
                <code class="rounded bg-gray-100 px-1 py-0.5 text-[13px]"
                  >REF</code
                >).
                <br />
                - Un nom sans nombre final peut aussi marcher (ex.
                <code class="rounded bg-gray-100 px-1 py-0.5 text-[13px]"
                  >MON-SKU-ROUGE.jpg</code
                >).
                <br />
                - Taille max : 15 Mo par image.
              </p>
              <p
                v-if="isAdminBulkNamedDragOver && !isAdminBulkNamedUploading"
                class="text-sm font-medium text-orange-800"
              >
                Relâchez pour envoyer les fichiers…
              </p>
              <p
                v-if="isAdminBulkNamedUploading"
                class="text-sm font-medium text-orange-800"
              >
                Traitement en cours…
              </p>
            </div>
            <button
              type="button"
              class="pointer-events-auto relative z-[1] shrink-0 self-start rounded-md border border-orange-300 bg-white px-3 py-2 text-sm font-medium text-orange-900 shadow-sm hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-normal text-center sm:text-left"
              :disabled="isAdminBulkNamedUploading"
              @click.stop="triggerAdminBulkNamedPhotosPicker"
              @dragenter.prevent="onAdminBulkNamedDragEnter"
              @dragover.prevent="onAdminBulkNamedDragOver"
              @dragleave.prevent="onAdminBulkNamedDragLeave"
              @drop.prevent="onAdminBulkNamedDrop"
            >
              Parcourir les fichiers
            </button>
          </div>
        </div>
      </div>
      <div
        v-if="
          adminRefsProduits.length > 0 &&
          (adminBulkNamedUploadBanner || adminBulkNamedUploadErrors.length)
        "
        class="px-4 py-2 border-b border-gray-200 bg-amber-50/90 text-sm"
      >
        <p v-if="adminBulkNamedUploadBanner" class="text-amber-900">
          {{ adminBulkNamedUploadBanner }}
        </p>
        <details
          v-if="adminBulkNamedUploadErrors.length"
          class="mt-1 text-amber-950"
        >
          <summary class="cursor-pointer font-medium">
            Détail des erreurs ({{ adminBulkNamedUploadErrors.length }})
          </summary>
          <ul
            class="mt-2 max-h-40 list-disc space-y-1 overflow-y-auto pl-5 text-xs"
          >
            <li v-for="(err, i) in adminBulkNamedUploadErrors" :key="i">
              {{ err }}
            </li>
          </ul>
        </details>
      </div>

      <div
        v-if="adminRefsTotal === 0 && !adminRefsReferenceFilter.trim()"
        class="p-8 text-center text-black text-sm"
      >
        Aucune référence pour ce shooting.
      </div>
      <div
        v-else-if="adminRefsProduits.length === 0"
        class="p-8 text-center text-black text-sm"
      >
        Aucune référence ne correspond au filtre.
      </div>
      <div v-else class="overflow-x-auto">
        <table
          class="admin-mel-refs-table w-full table-auto divide-y divide-gray-200 text-sm border-collapse min-w-[640px]"
        >
          <thead class="bg-gray-100 border-b border-gray-300">
            <tr>
              <th
                class="min-w-[10rem] max-w-[28%] px-3 py-2 text-left text-xs font-medium text-black uppercase tracking-wider align-top whitespace-normal"
              >
                Référence
              </th>
              <th
                class="min-w-[12rem] max-w-[32%] px-3 py-2 text-left text-xs font-medium text-black uppercase tracking-wider border-l border-gray-300 align-top whitespace-normal"
              >
                Données
              </th>
              <th
                class="min-w-[9rem] w-[17%] px-2 py-2 text-center text-xs font-medium text-black uppercase tracking-wider border-l border-gray-300 align-top whitespace-normal"
              >
                Actions
              </th>
              <th
                class="min-w-[14rem] px-2 py-2 text-center text-xs font-medium text-black uppercase tracking-wider border-l border-gray-300 align-top whitespace-normal"
              >
                Aperçu
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="(p, adminRefRowIdx) in adminRefsProduitsFiltered"
              :key="
                adminRefProductId(p) > 0
                  ? adminRefProductId(p)
                  : `admin-ref-row-${adminRefRowIdx}`
              "
              class="hover:bg-gray-50"
            >
              <td
                class="min-w-[10rem] max-w-[28%] px-3 py-2 align-top font-medium text-black min-w-0"
              >
                <div class="flex flex-wrap items-center gap-1.5 min-w-0">
                  <span
                    class="break-words [overflow-wrap:anywhere] whitespace-normal leading-snug"
                    :title="adminRefLabel(p)"
                  >
                    {{ adminRefLabel(p) }}
                  </span>
                  <span
                    :class="[
                      'inline-flex shrink-0 items-center px-2 py-0.5 rounded-full text-[10px] font-medium',
                      adminProductStatusBadgeClass(p),
                    ]"
                  >
                    {{ adminProductStatusLabel(p) }}
                  </span>
                </div>
                <dl class="mt-2 space-y-1 text-xs font-normal text-gray-700">
                  <div v-if="p.marque">Marque : {{ p.marque }}</div>
                  <div v-if="p.collection">Collection : {{ p.collection }}</div>
                  <div v-if="p.provenance">Provenance : {{ p.provenance }}</div>
                  <div v-if="p.tailles">Déclinaisons de taille : {{ p.tailles }}</div>
                  <div v-if="p.prix != null">Prix : {{ formatAdminProductNumber(p.prix, 'EUR') }}</div>
                  <div v-if="p.poids != null">Poids : {{ formatAdminProductNumber(p.poids) }} kg</div>
                </dl>
                <details class="mt-2 text-xs font-normal">
                  <summary class="cursor-pointer text-blue-700">Modifier prix / poids</summary>
                  <p class="my-2 text-gray-500">Appliqué à toutes les déclinaisons de cette référence de base.</p>
                  <label class="block mb-2">Prix (€)
                    <input type="number" min="0.01" step="0.01" :value="p.prix"
                      :disabled="adminSavingProductValues || adminPublishingDrafts"
                      class="w-full rounded border px-2 py-1"
                      @change="saveAdminProductValue(p, 'prix', $event)" />
                  </label>
                  <label class="block">Poids (kg)
                    <input type="number" min="0.001" step="0.001" :value="p.poids"
                      :disabled="adminSavingProductValues || adminPublishingDrafts"
                      class="w-full rounded border px-2 py-1"
                      @change="saveAdminProductValue(p, 'poids', $event)" />
                  </label>
                </details>
                <div
                  v-if="adminRefCommentaireAffiche(p)"
                  class="mt-2 rounded-md border border-amber-200 bg-amber-50/90 px-2 py-1.5 text-left"
                >
                  <div
                    class="text-[10px] font-semibold uppercase tracking-wide text-amber-800"
                  >
                    Commentaire
                  </div>
                  <p
                    class="mt-0.5 text-xs font-normal text-amber-950 whitespace-pre-wrap break-words [overflow-wrap:anywhere] leading-snug"
                  >
                    {{ adminRefCommentaireAffiche(p) }}
                  </p>
                </div>
              </td>
              <td
                class="min-w-[12rem] max-w-[32%] px-3 py-2 align-top text-xs text-black border-l border-gray-300 min-w-0"
              >
                <div class="space-y-1.5 text-xs min-w-0">
                  <p
                    v-if="!categoriesTree.length"
                    class="text-black italic leading-snug"
                  >
                    Chargement de l’arbre des catégories…
                  </p>
                  <template v-else>
                    <div class="flex flex-col gap-0.5 min-w-0">
                      <span class="font-medium text-black">Catégorie</span>
                      <select
                        class="w-full max-w-full rounded border border-gray-300 bg-white px-1.5 py-1 text-xs text-black whitespace-normal text-left focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400 disabled:opacity-60"
                        :value="adminRowCatParts(p).gen"
                        :disabled="isAdminSavingCategory(adminRefProductId(p))"
                        @change="onAdminCatGenChange(p, $event)"
                      >
                        <option value="">—</option>
                        <option
                          v-for="opt in adminRootCategorySelectOptions"
                          :key="opt.value"
                          :value="opt.value"
                        >
                          {{ opt.label }}
                        </option>
                      </select>
                    </div>
                    <div class="flex flex-col gap-0.5 min-w-0">
                      <span class="font-medium text-black"
                        >Sous-catégorie</span
                      >
                      <select
                        class="w-full max-w-full rounded border border-gray-300 bg-white px-1.5 py-1 text-xs text-black whitespace-normal text-left focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400 disabled:opacity-60"
                        :value="adminRowCatParts(p).sous"
                        :disabled="
                          !adminRowCatParts(p).gen ||
                          isAdminSavingCategory(adminRefProductId(p))
                        "
                        @change="onAdminCatSousChange(p, $event)"
                      >
                        <option value="">—</option>
                        <option
                          v-for="opt in adminSousCategorySelectOptions(
                            adminRowCatParts(p).gen,
                          )"
                          :key="opt.value"
                          :value="opt.value"
                        >
                          {{ opt.label }}
                        </option>
                      </select>
                    </div>
                    <div class="flex flex-col gap-0.5 min-w-0">
                      <span class="font-medium text-black"
                        >Sous-sous-catégorie</span
                      >
                      <select
                        class="w-full max-w-full rounded border border-gray-300 bg-white px-1.5 py-1 text-xs text-black whitespace-normal text-left focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400 disabled:opacity-60"
                        :value="adminRowCatParts(p).leaf"
                        :disabled="
                          !adminRowCatParts(p).sous ||
                          !adminSousSousCategorySelectOptions(
                            adminRowCatParts(p).gen,
                            adminRowCatParts(p).sous,
                          ).length ||
                          isAdminSavingCategory(adminRefProductId(p))
                        "
                        @change="onAdminCatSousSousChange(p, $event)"
                      >
                        <option value="">—</option>
                        <option
                          v-for="opt in adminSousSousCategorySelectOptions(
                            adminRowCatParts(p).gen,
                            adminRowCatParts(p).sous,
                          )"
                          :key="opt.value"
                          :value="opt.value"
                        >
                          {{ opt.label }}
                        </option>
                      </select>
                    </div>
                  </template>
                </div>
              </td>
              <td
                class="min-w-[9rem] w-[17%] px-1.5 py-2 align-top border-l border-gray-300 min-w-0"
              >
                <div
                  v-if="adminRefProductId(p) > 0"
                  :class="[
                    'border-2 border-dashed rounded-md px-2 py-3 text-center transition-all duration-200 select-none w-full min-w-0 min-h-[12rem] box-border flex flex-col items-center justify-center gap-2',
                    isAdminUploadDragOver(adminRefProductId(p))
                      ? 'border-blue-500 bg-blue-50 shadow-sm cursor-grabbing'
                      : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer bg-white',
                  ]"
                  @click="triggerAdminRefPhotoDialog(p)"
                  @dragenter.prevent="
                    adminUploadDragEnter($event, adminRefProductId(p))
                  "
                  @dragover.prevent="
                    adminUploadDragOver($event, adminRefProductId(p))
                  "
                  @dragleave.prevent="
                    adminUploadDragLeave($event, adminRefProductId(p))
                  "
                  @drop.prevent="adminUploadDrop($event, p)"
                >
                  <i
                    class="fas fa-cloud-upload-alt text-3xl shrink-0 leading-none transition-colors"
                    :class="
                      isAdminUploadDragOver(adminRefProductId(p))
                        ? 'text-blue-500'
                        : 'text-black'
                    "
                  ></i>
                  <template v-if="isAdminUploading(adminRefProductId(p))">
                    <span
                      class="flex flex-col items-center justify-center gap-1.5 text-xs text-blue-600 font-medium"
                    >
                      <span
                        class="inline-block w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin shrink-0"
                      ></span>
                      Envoi…
                    </span>
                  </template>
                  <template v-else>
                    <span
                      class="block w-full min-w-0 px-0.5 text-xs font-medium leading-snug text-black whitespace-normal text-center [overflow-wrap:anywhere] break-words"
                    >
                      {{
                        isAdminUploadDragOver(adminRefProductId(p))
                          ? "Déposez les fichiers ici"
                          : "Glisser-déposer ou cliquer pour envoyer"
                      }}
                    </span>
                  </template>
                </div>
                <div v-else class="text-[10px] text-black text-center">
                  —
                </div>
              </td>
              <td
                class="min-w-[14rem] px-2 py-2 align-stretch border-l border-gray-300 text-center min-w-0 align-top"
              >
                <div
                  class="min-h-[12rem] w-full flex flex-wrap items-center justify-center gap-2 content-center py-1"
                >
                  <template v-if="adminRefPhotoUrlsForProduct(p).length">
                    <div
                      v-for="(
                        photoUrl, photoIdx
                      ) in adminRefPhotoUrlsForProduct(p)"
                      :key="`${adminRefProductId(
                        p,
                      )}-${photoIdx}-${adminGetPhotoFilename(photoUrl)}`"
                      :draggable="!isAdminReorderingPhotos(adminRefProductId(p))"
                      :class="[
                        'relative group w-24 h-28 shrink-0 rounded bg-gray-50 overflow-hidden cursor-move transition-all',
                        photoIdx === 0
                          ? 'border-2 border-blue-500 shadow-md'
                          : 'border border-gray-200',
                        isAdminDraggingPhoto(adminRefProductId(p), photoIdx)
                          ? 'opacity-50 scale-95'
                          : '',
                        isAdminDragOverPhoto(adminRefProductId(p), photoIdx)
                          ? 'ring-2 ring-blue-400 ring-offset-1'
                          : '',
                      ]"
                      @dragstart="handleAdminPhotoDragStart($event, p, photoIdx)"
                      @dragend="handleAdminPhotoDragEnd()"
                      @dragover.prevent="
                        handleAdminPhotoItemDragOver(
                          $event,
                          adminRefProductId(p),
                          photoIdx,
                        )
                      "
                      @dragleave.prevent="handleAdminPhotoItemDragLeave()"
                      @drop.prevent="handleAdminPhotoDropOnItem($event, p, photoIdx)"
                    >
                      <img
                        :src="
                          adminRefPhotoDisplayUrl(
                            adminRefProductId(p),
                            photoUrl,
                          )
                        "
                        :alt="`Photo ${photoIdx + 1} ${adminRefLabel(p)}`"
                        class="w-full h-full object-cover pointer-events-none"
                        loading="lazy"
                      />
                      <div
                        v-if="photoIdx === 0"
                        class="absolute top-0 left-0 right-0 bg-blue-500 text-white text-[9px] font-semibold px-1 py-0.5 text-center leading-tight pointer-events-none z-[1]"
                      >
                        Photo de<br />couverture
                      </div>
                      <div
                        class="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 pointer-events-none z-[1]"
                        title="Glisser pour réorganiser"
                      >
                        <i class="fas fa-grip-vertical text-[8px]"></i>
                      </div>
                      <div
                        class="absolute inset-0 z-[2] bg-black/55 opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto grid grid-cols-2 gap-1 items-center justify-items-center content-center p-1.5"
                      >
                        <span
                          v-if="photoIdx === 0"
                          class="w-7 h-7 shrink-0"
                          aria-hidden="true"
                        />
                        <button
                          v-if="photoIdx !== 0"
                          type="button"
                          class="w-7 h-7 bg-white/90 text-yellow-500 rounded-full flex items-center justify-center focus:outline-none disabled:opacity-50"
                          title="Définir comme photo de couverture"
                          :disabled="
                            adminIsPhotoActionLoading(
                              adminRefProductId(p),
                              adminGetPhotoFilename(photoUrl),
                              'setCover',
                            )
                          "
                          @click.stop="handleAdminSetAsCover(p, photoUrl)"
                        >
                          <i class="fas fa-star text-xs"></i>
                        </button>
                        <button
                          type="button"
                          class="w-7 h-7 bg-white/90 text-blue-600 rounded-full flex items-center justify-center focus:outline-none disabled:opacity-50"
                          title="Pivoter la photo"
                          :disabled="
                            adminIsPhotoActionLoading(
                              adminRefProductId(p),
                              adminGetPhotoFilename(photoUrl),
                              'rotate',
                            )
                          "
                          @click.stop="handleAdminRotatePhoto(p, photoUrl)"
                        >
                          <i class="fas fa-redo-alt text-xs"></i>
                        </button>
                        <a
                          :href="
                            adminOriginalProductPhotoUrl(
                              adminRefProductId(p),
                              photoUrl,
                            )
                          "
                          target="_blank"
                          rel="noopener noreferrer"
                          class="w-7 h-7 bg-white/90 text-black rounded-full flex items-center justify-center focus:outline-none"
                          title="Voir en taille originale"
                          @click.stop
                        >
                          <i class="fas fa-download text-xs"></i>
                        </a>
                        <button
                          v-if="
                            !adminIsMainPhotoUrl(photoUrl) ||
                            adminPremelIsDraft(p)
                          "
                          type="button"
                          class="w-7 h-7 bg-white/90 text-red-600 rounded-full flex items-center justify-center focus:outline-none disabled:opacity-50"
                          title="Supprimer la photo"
                          :disabled="
                            adminIsPhotoActionLoading(
                              adminRefProductId(p),
                              adminGetPhotoFilename(photoUrl),
                              'delete',
                            )
                          "
                          @click.stop="handleAdminDeletePhoto(p, photoUrl)"
                        >
                          <i class="fas fa-trash-alt text-xs"></i>
                        </button>
                      </div>
                    </div>
                  </template>
                  <div
                    v-else
                    class="h-28 w-24 max-w-full flex flex-col items-center justify-center rounded border border-dashed border-gray-200 bg-gray-50 text-black shrink-0"
                    title="Aucune photo"
                  >
                    <i class="fas fa-image text-xl"></i>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="adminRefsTotal > 0"
        class="px-4 py-3 border-t border-gray-200"
      >
        <UnifiedPagination
          :total-items="adminRefsTotal"
          :current-page="adminRefsPage"
          :items-per-page="adminRefsLimit"
          variant="product"
          item-label="références"
          :items-per-page-options="[10, 25, 50, 100]"
          :show-first-last-buttons="true"
          :max-visible-pages="5"
          @page-change="handleAdminRefsPageChange"
          @items-per-page-change="handleAdminRefsLimitChange"
        />
      </div>
      <div class="border-t border-gray-200 p-4">
        <button class="rounded bg-emerald-600 px-4 py-2 font-semibold text-white disabled:opacity-50"
          :disabled="adminPublishingDrafts || adminSavingProductValues || isAdminBulkNamedUploading || adminUploadingProductIds.length > 0"
          @click="publishAdminDrafts">
          {{ adminPublishingDrafts ? 'Traitement…' : 'Mettre en ligne tous les produits avec photos en brouillon' }}
        </button>
        <p class="mt-2 text-xs text-gray-500">Toute la demande, toutes pages confondues, indépendamment du filtre de référence.</p>
      </div>
    </div>
  </div>

  <!-- Modal d'édition du shooting -->
  <ModalComp
    :isVisible="showEditModal"
    @update:isVisible="showEditModal = $event"
    size="xl"
    :hideFooter="true"
  >
    <template #header>
      <span>
        #{{ editingShootingData?.shooting?.id }} -
        {{
          editingShootingData?.produits &&
          editingShootingData.produits.length > 0
            ? editingShootingData.produits
                .slice(0, 5)
                .map((p: any) => p.reference)
                .join(", ") +
              (editingShootingData.produits.length > 5
                ? ` ... (+${editingShootingData.produits.length - 5})`
                : "")
            : "Aucune référence"
        }}
      </span>
    </template>

    <template #body>
      <div
        v-if="isLoadingEditData"
        class="flex justify-center items-center py-12"
      >
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
        ></div>
      </div>

      <div v-else-if="editingShootingData" class="space-y-4 modal-edit-zone">
        <!-- Titre : Nombre de pièces -->
        <div class="border-b pb-2">
          <h3 class="text-lg font-semibold">
            Nombre de pièces :
            {{ editingShootingData?.shooting?.totalPieces ?? 0 }} pcs
            <span class="text-sm text-black font-normal ml-3">
              ({{ editingShootingData?.counts?.produitsAffiches ?? 0 }}
              référence(s) groupée(s))
            </span>
          </h3>
        </div>

        <!-- Tableau des produits -->
        <div
          v-if="
            editingShootingData.produits &&
            editingShootingData.produits.length > 0
          "
          class="border border-gray-300 rounded overflow-x-auto"
        >
          <table class="min-w-full table-auto border-collapse">
            <thead class="bg-gray-100 border-b border-gray-300">
              <tr>
                <th
                  class="px-3 py-2 text-left text-sm font-semibold border-r border-gray-300"
                >
                  Référence
                </th>
                <th
                  class="px-3 py-2 text-left text-sm font-semibold border-r border-gray-300 w-32"
                >
                  Poids
                </th>
                <th
                  class="px-3 py-2 text-left text-sm font-semibold border-r border-gray-300"
                >
                  Données de la référence
                </th>
                <th class="px-3 py-2 text-center text-sm font-semibold w-24">
                  Action
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-300">
              <template
                v-for="produit in editingShootingData.produits"
                :key="produit.id_produit"
              >
                <tr class="hover:bg-gray-50">
                  <!-- Colonne 1 : Référence / Vendu par / Date -->
                  <td class="px-3 py-3 border-r border-gray-300 align-top">
                    <div class="space-y-1">
                      <div class="font-semibold text-sm">
                        {{ produit.reference_base || produit.reference }}
                      </div>
                      <div class="text-xs text-black">
                        {{
                          produit.vendu_par === "couleurs"
                            ? "À la couleur"
                            : produit.vendu_par === "tailles"
                            ? "Tailles aux choix"
                            : produit.vendu_par === "melangees"
                            ? "Couleurs mélangées"
                            : "-"
                        }}
                      </div>
                      <div class="text-xs text-black italic">
                        {{
                          produit.date_creation || produit.dateCreation
                            ? new Date(
                                produit.date_creation || produit.dateCreation,
                              ).toLocaleDateString("fr-FR") +
                              " " +
                              new Date(
                                produit.date_creation || produit.dateCreation,
                              ).toLocaleTimeString("fr-FR")
                            : "-"
                        }}
                      </div>
                    </div>
                  </td>

                  <!-- Colonne 2 : Poids -->
                  <td class="px-3 py-3 border-r border-gray-300 align-top">
                    <div class="text-sm text-black">
                      {{ produit.poids ? `${produit.poids} Kg` : "-- Kg --" }}
                    </div>
                  </td>

                  <!-- Colonne 3 : Données détaillées -->
                  <td
                    class="px-3 py-3 border-r border-gray-300 align-top max-w-xs"
                  >
                    <div class="text-xs space-y-1 break-words">
                      <div>
                        <span class="font-medium">Marque :</span>
                        {{ produit.marque || "-" }}
                      </div>
                      <div>
                        <span class="font-medium">Catégories :</span>
                        {{ produit.categorie_libelle || produit.id_categorie }}
                      </div>
                      <div>
                        <span class="font-medium">Collection :</span>
                        {{ produit.collection || "-" }}
                      </div>
                      <div v-if="produit.dimension">
                        <span class="font-medium">Dimensions du colis :</span>
                        {{ produit.dimension }} cm
                      </div>
                      <div>
                        <span class="font-medium">Provenance :</span>
                        {{ produit.provenance || "-" }}
                      </div>
                      <div>
                        <span class="font-medium"
                          >Déclinaisons de taille :</span
                        >
                        {{ produit.tailles || "-" }}
                      </div>
                      <div v-if="produit.vendu_par !== 'tailles'">
                        <span class="font-medium">Quantité par taille :</span>
                        {{ getPackDisplayLabel(produit) }}
                      </div>
                      <div>
                        <span class="font-medium">Prix :</span>
                        {{ produit.prix }}€
                      </div>
                      <div>
                        <span class="font-medium">Composition :</span>
                        {{ produit.composition || "-" }}
                      </div>
                      <div>
                        <span class="font-medium">Caractéristiques :</span>
                        <template
                          v-if="
                            produit.caracteristiques &&
                            Array.isArray(produit.caracteristiques) &&
                            produit.caracteristiques.length > 0
                          "
                        >
                          {{
                            produit.caracteristiques
                              .map((c: any) => c.libelle || c)
                              .join(", ")
                          }}
                        </template>
                        <span v-else>-</span>
                      </div>
                      <div
                        class="flex flex-wrap max-w-full"
                        :title="
                          Array.isArray(produit.couleurs)
                            ? produit.couleurs.join(', ')
                            : produit.couleurs || '-'
                        "
                      >
                        <span class="font-medium">Couleurs :</span>
                        <template v-if="Array.isArray(produit.couleurs)">
                          <template v-for="(c, i) in produit.couleurs" :key="i">
                            <span
                              :class="
                                i === 0 ? 'text-orange-500 font-bold' : ''
                              "
                              >{{ c }}</span
                            >
                            <span v-if="i < produit.couleurs.length - 1">
                              ,
                            </span>
                          </template>
                        </template>
                        <template v-else-if="produit.couleurs">
                          <template
                            v-for="(c, i) in produit.couleurs.split(',').map((s:any)=>s.trim())"
                            :key="i"
                          >
                            <span
                              :class="
                                i === 0 ? 'text-orange-500 font-bold' : ''
                              "
                              >{{ c }}</span
                            >
                            <span
                              v-if="i < produit.couleurs.split(',').length - 1"
                            >
                              ,
                            </span>
                          </template>
                        </template>
                        <span v-else>-</span>
                      </div>
                    </div>
                  </td>

                  <!-- Colonne 4 : Actions (masquées si shooting programmé ou confirmé) -->
                  <td class="px-3 py-3 text-center align-top">
                    <div class="flex flex-col gap-2">
                      <div
                        v-if="!isShootingReadOnly"
                        class="flex flex-col gap-2"
                      >
                        <button
                          v-if="editingProductId !== produit.id_produit"
                          @click="handleEditProduct(produit)"
                          class="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                          title="Modifier"
                        >
                          <i class="fas fa-edit"></i>
                        </button>
                        <button
                          @click="
                            handleDeleteProduct(
                              produit.id_produit,
                              produit.reference_base || produit.reference,
                            )
                          "
                          class="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                          title="Supprimer cette référence"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                      <button
                        v-if="
                          (editingShootingData?.shooting?.prestationValeur ===
                            5 ||
                            editingShootingData?.shooting?.prestationValeur ===
                              6) &&
                          (editingShootingData?.shooting?.finalisation ===
                            '1' ||
                            editingShootingData?.shooting?.finalisation === 1)
                        "
                        @click="handleProductPhotos(produit.id_produit)"
                        class="px-2 py-1 bg-teal-500 text-white text-xs rounded hover:bg-teal-600"
                        title="Télécharger les photos de cette référence"
                      >
                        <i class="fas fa-camera"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                <!-- Ligne d'édition inline -->
                <tr v-if="editingProductId === produit.id_produit">
                  <td
                    colspan="4"
                    class="px-4 py-4 edit-zone"
                    style="background-color: #fdf8e3"
                  >
                    <div
                      class="flex justify-between items-center mb-3 border-b pb-2"
                    >
                      <h4 class="text-base font-semibold text-black">
                        Modifier : {{ productFormData.reference }}
                      </h4>
                      <button
                        @click="handleCancelEditProduct"
                        class="text-black hover:text-black"
                      >
                        <i class="fas fa-times text-xl"></i>
                      </button>
                    </div>

                    <div v-if="isLoadingProductData" class="text-center py-8">
                      <div
                        class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                      ></div>
                      <p class="mt-2 text-sm text-black">Chargement...</p>
                    </div>

                    <form
                      v-else
                      @submit.prevent="handleSaveProduct"
                      class="space-y-2"
                    >
                      <!-- Informations de base -->
                      <div class="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4">
                        <BaseInput
                          v-model.trim="productFormData.reference"
                          label="Référence de base"
                          placeholder="Référence du produit"
                          required
                        />
                        <BaseInput
                          v-if="marques.length > 1"
                          v-model="productFormData.idVendeurMarque"
                          label="Marque"
                          type="select"
                          :options="marqueOptions"
                          required
                        />
                        <div>
                          <BaseInput
                            v-model="productFormData.poids"
                            label="Poids unitaire"
                            type="number"
                            placeholder="Poids"
                            required
                            step="0.001"
                            min="0.001"
                            :max="MAX_MEL_UNIT_WEIGHT_KG"
                          />
                          <p
                            v-if="
                              productFormData.poids &&
                              getMelUnitWeightError(productFormData.poids)
                            "
                            class="text-xs font-semibold text-red-600 mt-0.5"
                          >
                            {{ MAX_MEL_UNIT_WEIGHT_ERROR }}
                          </p>
                          <p class="text-xs text-red-600 mt-0.5">
                            Merci de bien vouloir augmenter légèrement le poids
                            de vos articles pour prendre en compte le poids des
                            cintres/cartons/éventuels surplus.
                          </p>
                        </div>
                      </div>

                      <!-- Catégories -->
                      <div class="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4">
                        <BaseInput
                          v-model="productFormData.idCategorieGenerale"
                          label="Catégories"
                          type="select"
                          :options="categorieOptions"
                          @change="handleCategorieChange"
                          required
                        />
                        <BaseInput
                          v-model="productFormData.idSousCategorie"
                          label="Sous-catégorie"
                          type="select"
                          :options="sousCategorieOptions"
                          :disabled="!productFormData.idCategorieGenerale"
                          @change="handleSousCategorieChange"
                          required
                        />
                        <BaseInput
                          v-model="productFormData.idCategorie"
                          label="Sous-sous-catégorie"
                          type="select"
                          :options="sousSousCategorieOptions"
                          :disabled="!productFormData.idSousCategorie"
                        />
                      </div>

                      <!-- Caractéristiques -->
                      <div
                        v-if="caracteristiques.length > 0"
                        class="bg-yellow-50 border border-gray-200 rounded-lg p-3"
                      >
                        <div
                          class="mb-2 cursor-pointer select-none"
                          @click="
                            caracteristiquesCollapsed =
                              !caracteristiquesCollapsed
                          "
                        >
                          <div class="flex items-center justify-between">
                            <h4 class="text-sm font-semibold text-black">
                              Caractéristiques
                            </h4>
                            <div class="flex items-center space-x-2">
                              <div
                                class="text-xs"
                                :class="
                                  productFormData.selectedCaracteristiques &&
                                  productFormData.selectedCaracteristiques
                                    .length === 0
                                    ? 'text-red-600 font-semibold'
                                    : 'text-black'
                                "
                              >
                                Sélectionnées:
                                {{
                                  productFormData.selectedCaracteristiques
                                    ? productFormData.selectedCaracteristiques
                                        .length
                                    : 0
                                }}/5
                              </div>
                              <svg
                                class="w-4 h-4 text-black transition-transform duration-200"
                                :class="{
                                  'rotate-90': !caracteristiquesCollapsed,
                                }"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          </div>
                          <p class="text-xs text-black italic mt-1">
                            Pour mieux vous trouver dans les filtres
                          </p>
                        </div>
                        <div
                          v-show="!caracteristiquesCollapsed"
                          class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2"
                        >
                          <label
                            v-for="carac in caracteristiques"
                            :key="carac.id_caracteristique"
                            class="flex items-center space-x-2 cursor-pointer hover:bg-white px-2 py-1 rounded text-xs border border-gray-200"
                          >
                            <input
                              type="checkbox"
                              :checked="
                                productFormData.selectedCaracteristiques &&
                                productFormData.selectedCaracteristiques.includes(
                                  carac.id_caracteristique,
                                )
                              "
                              :disabled="
                                productFormData.selectedCaracteristiques &&
                                !productFormData.selectedCaracteristiques.includes(
                                  carac.id_caracteristique,
                                ) &&
                                productFormData.selectedCaracteristiques
                                  .length >= 5
                              "
                              @change="
                                toggleProductCaracteristique(
                                  carac.id_caracteristique,
                                )
                              "
                              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3 h-3 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            <span
                              class="text-xs text-black break-words"
                              :class="{
                                'opacity-50':
                                  productFormData.selectedCaracteristiques &&
                                  !productFormData.selectedCaracteristiques.includes(
                                    carac.id_caracteristique,
                                  ) &&
                                  productFormData.selectedCaracteristiques
                                    .length >= 5,
                              }"
                              >{{ carac.libelle }}</span
                            >
                          </label>
                        </div>
                        <p
                          v-show="!caracteristiquesCollapsed"
                          class="text-xs mt-2"
                          :class="
                            productFormData.selectedCaracteristiques &&
                            productFormData.selectedCaracteristiques.length ===
                              0
                              ? 'text-red-600'
                              : 'text-black'
                          "
                        >
                          Maximum 5 caractéristiques
                        </p>
                      </div>

                      <!-- Composition -->
                      <CompositionForm
                        v-model="productFormData.compositions"
                        :compositionType="productFormData.compositionType"
                        :localisations="productFormData.localisations"
                        :compositionsList="productFormData.compositionsList"
                        :autocompleteOptions="compositionAutocompleteOptions"
                        :showAutocompleteDropdown="showCompositionDropdown"
                        :isLoadingAutocomplete="
                          isLoadingCompositionAutocomplete
                        "
                        @composition-input="handleCompositionInput"
                        @hide-dropdown="hideCompositionDropdown"
                        @select-autocomplete="selectComposition"
                      />

                      <!-- Vente, collection et pays -->
                      <div class="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4">
                        <BaseInput
                          v-model="productFormData.venduPar"
                          label="Vendu par"
                          type="select"
                          :options="venduParOptions"
                        />
                        <BaseInput
                          v-model="productFormData.idCollection"
                          label="Collection"
                          type="select"
                          :options="collectionOptions"
                          required
                        />
                        <BaseInput
                          v-model="productFormData.idProvenance"
                          label="Pays d'origine"
                          type="select"
                          :options="paysOptions"
                          required
                        />
                      </div>

                      <!-- Taille, quantité et stock -->
                      <div class="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4">
                        <BaseInput
                          v-model="productFormData.idDeclinaison"
                          label="Taille du paquet"
                          type="select"
                          :options="tailleOptions"
                          required
                        />
                        <BaseInput
                          v-if="productFormData.venduPar !== 'tailles'"
                          v-model="productFormData.idPack"
                          label="Quantité du paquet"
                          type="select"
                          :options="quantiteOptions"
                          required
                        />
                        <!-- Stock en paquet pour couleurs mélangées -->
                        <BaseInput
                          v-if="
                            productFormData.venduPar === 'melangees' &&
                            productFormData.idDeclinaison
                          "
                          v-model="productFormData.stock"
                          label="Stock en paquet (optionnel)"
                          type="number"
                          min="0"
                          placeholder="0"
                        />

                        <!-- Stock en paquet pour couleurs / tailles : bouton ouvrant le modal -->
                        <div
                          v-if="
                            (productFormData.venduPar === 'couleurs' ||
                              productFormData.venduPar === 'tailles') &&
                            productFormData.idDeclinaison
                          "
                          class="flex flex-col justify-end"
                        >
                          <label
                            class="block text-sm font-medium text-black mb-1"
                            >Stock en paquet
                            <span class="text-xs text-black font-normal"
                              >(optionnel)</span
                            ></label
                          >
                          <button
                            type="button"
                            @click="showStockModal = true"
                            class="inline-flex items-center gap-2 px-3 py-2 border border-blue-500 rounded-md text-sm text-blue-600 bg-white hover:bg-blue-50 transition-colors"
                          >
                            <i class="fas fa-boxes text-xs"></i>
                            Gérer le stock
                          </button>
                        </div>
                      </div>

                      <!-- Prix et remises -->
                      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <BaseInput
                          v-model="productFormData.prix"
                          label="Prix HT"
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          required
                        />
                        <BaseInput
                          v-model="productFormData.prixReduit"
                          label="Prix réduit HT"
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          :max="
                            productFormData.prix
                              ? (
                                  parseFloat(productFormData.prix) - 0.01
                                ).toFixed(2)
                              : undefined
                          "
                        />
                        <BaseInput
                          v-model="productFormData.dateRemise"
                          label="Date de début de remise"
                          type="date"
                          :min="todayDate"
                        />
                        <BaseInput
                          v-model="productFormData.pourcentageRemise"
                          label="Pourcentage de remise (%)"
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          placeholder="0"
                        />
                      </div>

                      <!-- Dimensions et minimum -->
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div
                          v-if="Number(productFormData.idCategorieGenerale) === 15 || isProductShoe"
                        >
                          <BaseInput
                            v-model="productFormData.dimensions"
                            :label="isProductShoe ? 'Dimensions du colis (cm, facultatif)' : 'Dimensions (Facultatif)'"
                          />
                          <p class="text-xs text-black mt-0.5">
                            Longueur × largeur × hauteur
                          </p>
                        </div>

                        <div v-if="productFormData.venduPar === 'tailles'">
                          <BaseInput
                            v-model="productFormData.qteMini"
                            label="Minimum de commande (Facultatif)"
                            type="number"
                            placeholder="Nombre minimum"
                          />
                          <p class="text-xs text-black mt-0.5">
                            Nombre min. de pièces à commander
                          </p>
                        </div>
                      </div>

                      <!-- Couleurs -->
                      <CouleursSelector
                        v-model="productFormData.selectedCouleurs"
                        :couleursList="couleurs"
                        :taillesList="taillesListForMelangeesEdit"
                      />

                      <!-- Message informatif pour les couleurs mélangées -->
                      <div
                        v-if="productFormData.venduPar === 'melangees'"
                        class="mt-2 p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r"
                      >
                        <div
                          v-if="!productFormData.idDeclinaison"
                          class="text-sm text-blue-700"
                        >
                          ⚠️ Veuillez sélectionner la déclinaison de taille pour
                          pouvoir spécifier les quantités par couleur
                        </div>
                        <div
                          v-else-if="
                            productFormData.selectedCouleurs.length <= 1
                          "
                          class="text-sm text-blue-700"
                        >
                          ⚠️ Veuillez ajouter d'autres couleurs
                        </div>
                        <div v-else class="text-xs text-blue-600">
                          💡 Pour chaque couleur, spécifiez la quantité par
                          taille (ex: 1 Rouge en S, 2 en M, 1 en L)
                        </div>
                      </div>

                      <!-- Descriptions + AI Generate -->
                      <div class="space-y-4">
                        <div class="flex items-center justify-between">
                          <label class="block text-sm font-medium text-black">Descriptions ( Détection automatique )</label>
                          <button
                            type="button"
                            @click="autoTranslateListMel"
                            :disabled="isTranslatingListMel"
                            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <svg v-if="isTranslatingListMel" class="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                            </svg>
                            <span v-else>🤖</span>
                            {{ isTranslatingListMel ? 'Translating...' : 'AI Traduction' }}
                          </button>
                        </div>
                        <textarea
                          v-model="descriptionInputListMel"
                          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-black placeholder-gray-400"
                          rows="3"
                          placeholder="Entrez la description, puis cliquez AI Traduction"
                        ></textarea>
                        <div
                          v-if="productFormData.descriptionFr || productFormData.descriptionEn || productFormData.descriptionIt || productFormData.descriptionEs || productFormData.descriptionDe"
                          class="grid grid-cols-1 md:grid-cols-2 gap-4"
                        >
                          <div>
                            <label class="block text-sm font-medium text-black mb-1">Français</label>
                            <textarea v-model="productFormData.descriptionFr" class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-black" rows="3"></textarea>
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-black mb-1">English</label>
                            <textarea v-model="productFormData.descriptionEn" class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-black" rows="3"></textarea>
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-black mb-1">Italiano</label>
                            <textarea v-model="productFormData.descriptionIt" class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-black" rows="3"></textarea>
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-black mb-1">Español</label>
                            <textarea v-model="productFormData.descriptionEs" class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-black" rows="3"></textarea>
                          </div>
                          <div>
                            <label class="block text-sm font-medium text-black mb-1">Deutsch</label>
                            <textarea v-model="productFormData.descriptionDe" class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-black" rows="3"></textarea>
                          </div>
                        </div>
                      </div>

                      <!-- Message d'erreur quantités mélangées -->
                      <div
                        v-if="melangeesQuantitesError"
                        class="p-3 bg-red-50 border-l-4 border-red-500 rounded-r text-sm text-red-700"
                      >
                        ⚠️ {{ melangeesQuantitesError }}
                      </div>

                      <!-- Boutons d'action -->
                      <div
                        class="pt-4 border-t border-gray-200 flex justify-end gap-3 action-buttons"
                      >
                        <button
                          type="button"
                          @click="handleCancelEditProduct"
                          class="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Annuler
                        </button>
                        <button
                          type="submit"
                          :disabled="isSavingProduct || !isProductFormValid"
                          class="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          <span
                            v-if="isSavingProduct"
                            class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                          ></span>
                          Enregistrer les modifications
                        </button>
                      </div>
                    </form>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div
          v-else
          class="text-center py-8 text-black border border-gray-300 rounded"
        >
          Aucun produit dans ce shooting
        </div>
      </div>
    </template>
  </ModalComp>

  <!-- Modal Stock en paquet (couleurs / tailles) -->
  <ModalComp
    :isVisible="showStockModal"
    :title="
      productFormData.venduPar === 'tailles'
        ? 'Stock par taille'
        : 'Stock en paquet'
    "
    size="lg"
    hideFooter
    @update:isVisible="showStockModal = $event"
  >
    <template #body>
      <p class="text-xs text-black font-normal mb-4">(optionnel)</p>

      <!-- Pour couleurs : stock par couleur -->
      <div
        v-if="
          productFormData.venduPar === 'couleurs' &&
          productFormData.selectedCouleurs.length > 0
        "
        class="space-y-2"
      >
        <div
          v-for="c in productFormData.selectedCouleurs"
          :key="c.id"
          class="p-3 border border-gray-200 rounded-md bg-gray-50 flex items-center gap-3"
        >
          <span
            class="inline-block w-3 h-3 rounded-full border border-gray-500 flex-shrink-0"
            :style="{ backgroundColor: (c as any).hexColor || '#CCCCCC' }"
          ></span>
          <span class="text-sm font-medium text-black w-28 truncate">{{
            c.nom
          }}</span>
          <input
            :value="(c as any).stock != null ? String((c as any).stock) : ''"
            @input="(e) => { (c as any).stock = (e.target as HTMLInputElement).value === '' ? null : Number((e.target as HTMLInputElement).value) }"
            type="number"
            min="0"
            class="w-24 px-2 py-1 text-sm border border-gray-500 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center text-black"
            placeholder="0"
          />
        </div>
      </div>

      <!-- Pour tailles : stock par couleur × taille -->
      <div
        v-else-if="
          productFormData.venduPar === 'tailles' &&
          productFormData.selectedCouleurs.length > 0
        "
        class="space-y-3"
      >
        <div
          v-for="c in productFormData.selectedCouleurs"
          :key="c.id"
          class="flex items-center gap-3 px-3 py-2 border border-gray-200 rounded-md bg-gray-50"
        >
          <div class="flex items-center gap-2 flex-shrink-0 w-28">
            <span
              class="inline-block w-3 h-3 rounded-full border border-gray-500 flex-shrink-0"
              :style="{ backgroundColor: (c as any).hexColor || '#CCCCCC' }"
            ></span>
            <span class="text-sm font-medium text-black truncate">{{
              c.nom
            }}</span>
          </div>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="(ts, idx) in (c as any).tailleStocks"
              :key="ts.taille"
              class="flex items-center gap-1"
            >
              <span class="text-xs text-black">{{ ts.taille }}</span>
              <input
                :value="ts.quantity != null ? String(ts.quantity) : ''"
                @input="(e) => { (c as any).tailleStocks[idx].quantity = (e.target as HTMLInputElement).value === '' ? null : Number((e.target as HTMLInputElement).value) }"
                type="number"
                min="0"
                class="w-16 px-2 py-1 text-sm border border-gray-500 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center text-black"
                placeholder="0"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-sm text-black text-center py-4">
        Aucune couleur sélectionnée.
      </div>

      <div class="flex justify-end mt-6">
        <button
          type="button"
          @click="showStockModal = false"
          class="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
        >
          Confirmer
        </button>
      </div>
    </template>
  </ModalComp>

  <!-- Modal de duplication batch depuis la liste principale -->
  <ModalComp v-model:isVisible="duplicateSelectVisible" size="xl">
    <template #header>
      <h3 class="text-base font-semibold text-black">Dupliquer les produits</h3>
    </template>
    <template #body>
      <!-- Loading -->
      <div v-if="duplicateSelectLoading" class="text-center py-10">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-gray-600"></div>
        <p class="mt-2 text-sm text-black">Chargement...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="duplicateSelectProducts.length === 0" class="text-center py-10">
        <p class="text-sm text-black">Aucun produit trouvé dans ce shooting.</p>
      </div>

      <!-- Content -->
      <div v-else class="space-y-3">
        <!-- Select all -->
        <div class="flex items-center justify-between px-1 py-2">
          <label class="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              :checked="duplicateCheckedIds.size === duplicateSelectProducts.length && duplicateSelectProducts.length > 0"
              :indeterminate="duplicateCheckedIds.size > 0 && duplicateCheckedIds.size < duplicateSelectProducts.length"
              @change="toggleSelectAll"
              class="w-4 h-4 text-black border-gray-300 rounded focus:ring-gray-400"
            />
            <span class="text-sm text-black">Tout sélectionner</span>
          </label>
          <span class="text-xs text-black">{{ duplicateCheckedIds.size }}/{{ duplicateSelectProducts.length }} sélectionné(s)</span>
        </div>

        <!-- Global settings -->
        <div v-if="duplicateCheckedIds.size > 0" class="border border-gray-200 rounded-lg divide-y divide-gray-200">
          <!-- Prefix & Suffix -->
          <div class="flex items-center gap-3 px-3 py-2.5">
            <label class="text-xs font-medium text-black whitespace-nowrap w-24">Préfixe / Suffixe</label>
            <div class="flex-1 grid grid-cols-2 gap-2">
              <input
                v-model.trim="duplicatePrefix"
                type="text"
                placeholder="Préfixe — Ex: COPY-, NEW-"
                class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
              />
              <input
                v-model.trim="duplicateSuffix"
                type="text"
                placeholder="Suffixe — Ex: -V2, -NEW"
                class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
              />
            </div>
            <button
              @click="applyPrefixSuffixToAll"
              class="px-3 py-1.5 text-xs font-medium text-black bg-gray-100 rounded-md hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              Appliquer
            </button>
          </div>
          <!-- Global category -->
          <div class="px-3 py-2.5">
            <div class="flex items-center gap-3">
              <label class="text-xs font-medium text-black whitespace-nowrap w-24">Catégorie globale</label>
              <div class="flex-1 grid grid-cols-3 gap-2">
                <select
                  v-model="duplicateGlobalCatGen"
                  @change="onGlobalCatGenChange"
                  class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
                >
                  <option value="">— Catégorie —</option>
                  <option
                    v-for="cat in adminRootCategorySelectOptions"
                    :key="cat.value"
                    :value="cat.value"
                  >{{ cat.label }}</option>
                </select>
                <select
                  v-model="duplicateGlobalCatSous"
                  @change="onGlobalCatSousChange"
                  :disabled="!duplicateGlobalCatGen"
                  class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-400 focus:border-gray-400 disabled:bg-gray-50 disabled:text-black"
                >
                  <option value="">— Sous-cat. —</option>
                  <option
                    v-for="cat in adminSousCategorySelectOptions(duplicateGlobalCatGen)"
                    :key="cat.value"
                    :value="cat.value"
                  >{{ cat.label }}</option>
                </select>
                <select
                  v-model="duplicateGlobalCatLeaf"
                  :disabled="!duplicateGlobalCatSous"
                  class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-400 focus:border-gray-400 disabled:bg-gray-50 disabled:text-black"
                >
                  <option value="">— Sous-sous-cat. —</option>
                  <option
                    v-for="cat in adminSousSousCategorySelectOptions(duplicateGlobalCatGen, duplicateGlobalCatSous)"
                    :key="cat.value"
                    :value="cat.value"
                  >{{ cat.label }}</option>
                </select>
              </div>
              <button
                @click="applyGlobalCategoryToAll"
                :disabled="!duplicateGlobalCatGen"
                class="px-3 py-1.5 text-xs font-medium text-black bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-40 transition-colors whitespace-nowrap"
              >
                Appliquer
              </button>
            </div>
          </div>
          <!-- Global marque -->
          <div v-if="marqueOptions.length >= 1" class="px-3 py-2.5">
            <div class="flex items-center gap-3">
              <label class="text-xs font-medium text-black whitespace-nowrap w-24">Marque globale</label>
              <div class="flex-1">
                <select
                  v-model="duplicateGlobalMarque"
                  class="w-full px-2 py-1.5 text-xs text-center border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
                >
                  <option value="">— Marque —</option>
                  <option
                    v-for="m in marqueOptions"
                    :key="m.value"
                    :value="m.value"
                  >{{ m.label }}</option>
                </select>
              </div>
              <button
                @click="applyGlobalMarqueToAll"
                :disabled="!duplicateGlobalMarque"
                class="px-3 py-1.5 text-xs font-medium text-black bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-40 transition-colors whitespace-nowrap"
              >
                Appliquer
              </button>
            </div>
          </div>
        </div>

        <!-- Product list header -->
        <div class="grid gap-2 px-3 py-1.5 text-[10px] font-medium text-black uppercase tracking-wider"
             style="grid-template-columns: 28px 1fr 16px 1fr 1fr 1fr 1fr 1fr;">
          <div></div>
          <div>Réf. originale</div>
          <div></div>
          <div>Nouvelle réf.</div>
          <div>Marque</div>
          <div>Catégorie</div>
          <div>Sous-cat.</div>
          <div>Sous-sous-cat.</div>
        </div>

        <!-- Product list -->
        <div class="max-h-64 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
          <div
            v-for="(p, index) in duplicateSelectProducts"
            :key="p.id_produit"
            class="grid gap-2 items-center px-3 py-2 transition-colors"
            style="grid-template-columns: 28px 1fr 16px 1fr 1fr 1fr 1fr 1fr;"
            :class="duplicateCheckedIds.has(p.id_produit) ? 'bg-gray-50' : 'bg-white'"
          >
            <!-- Checkbox -->
            <div class="flex justify-center">
              <input
                type="checkbox"
                :checked="duplicateCheckedIds.has(p.id_produit)"
                @change="toggleProductCheck(p.id_produit)"
                class="w-3.5 h-3.5 text-black border-gray-300 rounded focus:ring-gray-400"
              />
            </div>
            <!-- Original ref -->
            <div class="flex items-center gap-1 min-w-0">
              <span class="text-[10px] text-black flex-shrink-0">{{ index + 1 }}.</span>
              <span class="text-xs text-black truncate" :title="p.reference_base || p.reference">
                {{ p.reference_base || p.reference || '—' }}
              </span>
            </div>
            <!-- Arrow -->
            <div class="text-center">
              <i class="fas fa-arrow-right text-[9px] text-black"></i>
            </div>
            <!-- New reference -->
            <div>
              <input
                v-model.trim="duplicateNewRefs[p.id_produit]"
                type="text"
                :placeholder="duplicateCheckedIds.has(p.id_produit) ? 'Nouvelle réf.' : '—'"
                :disabled="!duplicateCheckedIds.has(p.id_produit)"
                class="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-400 focus:border-gray-400 disabled:bg-gray-50 disabled:text-black disabled:border-gray-200"
              />
            </div>
            <!-- Marque -->
            <div>
              <select
                v-model="duplicateMarque[p.id_produit]"
                :disabled="!duplicateCheckedIds.has(p.id_produit)"
                class="w-full px-1.5 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-400 focus:border-gray-400 disabled:bg-gray-50 disabled:text-black disabled:border-gray-200"
              >
                <option value="">—</option>
                <option
                  v-for="m in marqueOptions"
                  :key="m.value"
                  :value="m.value"
                >{{ m.label }}</option>
              </select>
            </div>
            <!-- Catégorie -->
            <div>
              <select
                v-model="duplicateCatGen[p.id_produit]"
                @change="onDupCatGenChange(p.id_produit)"
                :disabled="!duplicateCheckedIds.has(p.id_produit)"
                class="w-full px-1.5 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-400 focus:border-gray-400 disabled:bg-gray-50 disabled:text-black disabled:border-gray-200"
              >
                <option value="">—</option>
                <option
                  v-for="cat in adminRootCategorySelectOptions"
                  :key="cat.value"
                  :value="cat.value"
                >{{ cat.label }}</option>
              </select>
            </div>
            <!-- Sous-catégorie -->
            <div>
              <select
                v-model="duplicateCatSous[p.id_produit]"
                @change="onDupCatSousChange(p.id_produit)"
                :disabled="!duplicateCheckedIds.has(p.id_produit) || !duplicateCatGen[p.id_produit]"
                class="w-full px-1.5 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-400 focus:border-gray-400 disabled:bg-gray-50 disabled:text-black disabled:border-gray-200"
              >
                <option value="">—</option>
                <option
                  v-for="cat in adminSousCategorySelectOptions(duplicateCatGen[p.id_produit])"
                  :key="cat.value"
                  :value="cat.value"
                >{{ cat.label }}</option>
              </select>
            </div>
            <!-- Sous-sous-catégorie -->
            <div>
              <select
                v-model="duplicateCatLeaf[p.id_produit]"
                :disabled="!duplicateCheckedIds.has(p.id_produit) || !duplicateCatSous[p.id_produit]"
                class="w-full px-1.5 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-400 focus:border-gray-400 disabled:bg-gray-50 disabled:text-black disabled:border-gray-200"
              >
                <option value="">—</option>
                <option
                  v-for="cat in adminSousSousCategorySelectOptions(duplicateCatGen[p.id_produit], duplicateCatSous[p.id_produit])"
                  :key="cat.value"
                  :value="cat.value"
                >{{ cat.label }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex items-center justify-end gap-4 w-full">
        <p class="text-xs text-black mr-auto">Variantes couleur et photos incluses.</p>
        <button
          @click="duplicateSelectVisible = false"
          class="text-sm text-black hover:text-black transition-colors"
        >
          Annuler
        </button>
        <button
          @click="confirmBatchDuplicate"
          :disabled="!canConfirmBatchDuplicate || isDuplicating"
          class="px-4 py-2 bg-gray-800 text-white text-sm font-medium rounded-md hover:bg-gray-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <span v-if="isDuplicating">
            <i class="fas fa-spinner fa-spin mr-1"></i> Duplication...
          </span>
          <span v-else>Dupliquer ({{ duplicateCheckedIds.size }})</span>
        </button>
      </div>
    </template>
  </ModalComp>

  <!-- Modal de duplication -->
  <ModalComp v-model:isVisible="duplicateConfirmVisible">
    <template #header>
      <h3 class="text-lg font-semibold">Dupliquer le produit</h3>
    </template>
    <template #body>
      <p class="text-sm text-black mb-4">
        Vous allez dupliquer le produit <strong>{{ duplicateOriginalRef }}</strong> avec toutes ses variantes couleur.
        Veuillez saisir une nouvelle référence :
      </p>
      <BaseInput
        v-model.trim="duplicateNewReference"
        label="Nouvelle référence"
        placeholder="Saisir la nouvelle référence"
        required
      />
    </template>
    <template #footer>
      <div class="flex justify-end gap-2">
        <button
          @click="duplicateConfirmVisible = false"
          class="px-4 py-2 bg-gray-300 text-black text-sm rounded-md hover:bg-gray-400"
        >
          Annuler
        </button>
        <button
          @click="confirmDuplicateProduct"
          :disabled="!duplicateNewReference || isDuplicating"
          class="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
        >
          <span v-if="isDuplicating">Duplication...</span>
          <span v-else>Dupliquer</span>
        </button>
      </div>
    </template>
  </ModalComp>

  <!-- Modal de confirmation : date de shooting (admin) -->
  <ModalComp v-model:isVisible="shootingConfirmationVisible">
    <template #header>
      <h3 class="text-lg font-semibold">Confirmer le shooting</h3>
    </template>
    <template #body>
      <p class="text-sm text-black mb-3">
        Choisissez la date de shooting pour la demande
        <strong v-if="shootingConfirmationId">#{{ shootingConfirmationId }}</strong>.
      </p>
      <label class="block text-sm font-medium text-black mb-1">
        Date de shooting
      </label>
      <input
        v-model="shootingConfirmationDate"
        type="date"
        :min="todayDate"
        class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
      />
    </template>
    <template #footer>
      <div class="flex justify-end gap-2">
        <button
          type="button"
          @click="shootingConfirmationVisible = false"
          class="px-4 py-2 bg-gray-300 text-black text-sm rounded-md hover:bg-gray-400"
        >
          Annuler
        </button>
        <button
          type="button"
          :disabled="!shootingConfirmationDate || confirmingShootingId !== null"
          @click="confirmShootingDate"
          class="px-4 py-2 bg-teal-600 text-white text-sm rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="confirmingShootingId !== null">Confirmation...</span>
          <span v-else>Confirmer</span>
        </button>
      </div>
    </template>
  </ModalComp>

  <!-- Modal de confirmation : finalisation -->
  <ModalComp v-model:isVisible="finalisationVisible">
    <template #header>
      <h3 class="text-lg font-semibold">Finaliser le shooting</h3>
    </template>
    <template #body>
      <p class="text-sm text-black">
        Confirmer la finalisation de ce shooting ?
      </p>
    </template>
    <template #footer>
      <div class="flex justify-end gap-2">
        <button
          @click="finalisationVisible = false"
          class="px-4 py-2 bg-gray-300 text-black text-sm rounded-md hover:bg-gray-400"
        >
          Annuler
        </button>
        <button
          @click="confirmFinalisation"
          class="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
        >
          Confirmer
        </button>
      </div>
    </template>
  </ModalComp>

  <!-- Modal de confirmation : annulation de la finalisation -->
  <ModalComp v-model:isVisible="cancelFinalisationVisible">
    <template #header>
      <h3 class="text-lg font-semibold">Annuler la finalisation</h3>
    </template>
    <template #body>
      <p class="text-sm text-black">
        Confirmer l'annulation de la finalisation de ce shooting ?
      </p>
    </template>
    <template #footer>
      <div class="flex justify-end gap-2">
        <button
          @click="cancelFinalisationVisible = false"
          class="px-4 py-2 bg-gray-300 text-black text-sm rounded-md hover:bg-gray-400"
        >
          Annuler
        </button>
        <button
          @click="confirmCancelFinalisation"
          class="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
        >
          Confirmer
        </button>
      </div>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import {
  PageHeader,
  BaseInput,
  ReusableTable,
  FiltersCompact,
  ModalComp,
  CouleursSelector,
  CompositionForm,
  UnifiedPagination,
} from "@/components";
import type { MelFilters, DemandeMiseEnLigne } from "@/types";
import {
  getMyShootings,
  downloadShootingPhotos,
  downloadProductPhotos,
  getShootingForEdit,
  deleteProduct,
  updateProduct,
  getReferenceData,
  getCaracteristiques,
  getCompositionAutocomplete,
  uploadProductPhotosBatched,
  productPhotoInputAccept,
  isSupportedProductPhotoFormat,
  isSupportedProductPhotoSize,
  getUnsupportedProductPhotoErrorMessage,
  getProductPhotosBatch,
  deleteProductPhoto,
  rotateProductPhoto,
  reorderProductPhotos,
  confirmerShooting,
  finaliserShooting,
  getShootingPickupLabelUrl,
  getShootingPickupLabelConfiguration,
  saveShootingPickupLabelConfiguration,
} from "@/api/shooting";
import {
  fetchRest,
  getApiBaseUrl,
  getProductImageUrl,
  getProductOriginalImageUrl,
} from "@/config/api";
import { getCouleursOfficielles } from "@/api/couleurs";
import { duplicateProduitForMel } from "@/api/produits";
import { ManagerShootingService } from "@/services/manager-shooting.service";
import { useNotification } from "@/composables/useNotification";
import { useAuthStore } from "@/stores/auth";
import { getCategoriesTree } from "@/api/categories";
import type { CategorieNode } from "@/types/categorie";
import { bumpPhotoVersion, getPhotoVersion } from "@/utils/photoVersion";
import { getPackLength } from "@/utils/packLength";
import {
  formatDeclinaisonLabel,
  getDeclinaisonLength,
} from "@/utils/declinaisonLabel";
import {
  isShoeCategory,
  isValidProductDimensions,
} from "@/utils/productDimensions";
import {
  MAX_MEL_UNIT_WEIGHT_KG,
  MAX_MEL_UNIT_WEIGHT_ERROR,
  getMelUnitWeightError,
  isMelUnitWeightAllowed,
} from "@/utils/melCompositions";

// Types
type Demande = DemandeMiseEnLigne & { pickup?: number | string | null };
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const { success, error: showError } = useNotification();
const pickupLabelModalVisible = ref(false);
const pickupLabelShootingId = ref<number | null>(null);
const isLoadingPickupLabelConfiguration = ref(false);
const isSavingPickupLabelConfiguration = ref(false);
const pickupPackageTypes = [
  { id: 1, label: "Sac eFashion" },
  { id: 2, label: "Carton" },
  { id: 3, label: "Sac plastique" },
  { id: 4, label: "Sac grossiste" },
];
const pickupHangerTypes = [
  { id: 5, label: "Avec cintre" },
  { id: 6, label: "Sans cintre" },
];
const selectedPickupPackages = reactive<Record<number, boolean>>({
  1: false,
  2: false,
  3: false,
  4: false,
});
const pickupPackageQuantities = reactive<Record<number, number>>({
  1: 1,
  2: 1,
  3: 1,
  4: 1,
});
const pickupHangerTypeId = ref<number | null>(null);
const pickupLabelCount = computed(() =>
  pickupPackageTypes.reduce(
    (total, type) =>
      total +
      (selectedPickupPackages[type.id]
        ? Number(pickupPackageQuantities[type.id]) || 0
        : 0),
    0,
  ),
);

// Colonnes du tableau
const tableColumns = [
  { key: "id", title: "#", sortable: true, align: "left" as const },
  {
    key: "typeShooting",
    title: "Type",
    sortable: true,
    align: "left" as const,
  },
  {
    key: "dateDemande",
    title: "Demandé le",
    sortable: true,
    align: "left" as const,
  },
  {
    key: "categories",
    title: "Catégories",
    sortable: true,
    align: "left" as const,
  },
  {
    key: "dateConfirmation",
    title: "Confirmé le",
    sortable: true,
    align: "left" as const,
  },
  {
    key: "dateShooting",
    title: "Shooting le",
    sortable: true,
    align: "left" as const,
  },
  {
    key: "totalPieces",
    title: "Pièces",
    sortable: true,
    align: "right" as const,
  },
];

// État réactif
const isFiltering = ref(false);
const isLoading = ref(true);
const demandes = ref<Demande[]>([]);
const currentPage = ref(1);
const itemsPerPage = ref(25);
const totalItems = ref(0);
const totalPages = ref(0);

/** Mode admin (impersonation) : n’afficher que les références d’une demande. */
const adminRefsShootingId = ref<number | null>(null);
const adminRefsPayload = ref<any>(null);
const adminRefsLoading = ref(false);
/** Filtre texte sur la colonne référence (vue admin) — filtrage serveur. */
const adminRefsReferenceFilter = ref("");
const adminRefsPage = ref(1);
const adminRefsLimit = ref(25);
const adminRefsTotal = ref(0);
let adminRefsFilterDebounce: ReturnType<typeof setTimeout> | null = null;
let adminRefsIgnoreFilterWatch = false;

/** Zone « photos en masse » : même convention que le ZIP `{référence}-{0..19}.ext`, ou nom = référence (règles historiques). */
const adminBulkNamedPhotosInputRef = ref<HTMLInputElement | null>(null);
const isAdminBulkNamedUploading = ref(false);
const adminBulkNamedUploadErrors = ref<string[]>([]);
const adminBulkNamedUploadBanner = ref("");

// Duplicate product state
const duplicateConfirmVisible = ref(false);
const duplicateProductId = ref<number | null>(null);
const duplicateOriginalRef = ref("");
const duplicateNewReference = ref("");
const isDuplicating = ref(false);

// Duplicate from shooting list (batch mode)
const duplicateSelectVisible = ref(false);
const duplicateSelectLoading = ref(false);
const duplicateSelectProducts = ref<any[]>([]);
const duplicateCheckedIds = ref<Set<number>>(new Set());
const duplicateNewRefs = ref<Record<number, string>>({});
const duplicatePrefix = ref("");
const duplicateSuffix = ref("");
const duplicateCatGen = ref<Record<number, string>>({});
const duplicateCatSous = ref<Record<number, string>>({});
const duplicateCatLeaf = ref<Record<number, string>>({});
const duplicateGlobalCatGen = ref("");
const duplicateGlobalCatSous = ref("");
const duplicateGlobalCatLeaf = ref("");
const duplicateMarque = ref<Record<number, string | number>>({});
const duplicateGlobalMarque = ref<string | number>("");
const isAdminBulkNamedDragOver = ref(false);

const adminSavingProductValues = ref(false);
const adminPublishingDrafts = ref(false);
const formatAdminProductNumber = (value: any, currency?: string) =>
  new Intl.NumberFormat('fr-FR', currency
    ? { style: 'currency', currency }
    : { maximumFractionDigits: 3 }).format(Number(value));

async function saveAdminProductValue(p: any, field: 'prix' | 'poids', event: Event) {
  const input = event.target as HTMLInputElement;
  const value = Number(input.value);
  if (adminSavingProductValues.value || !input.value || !Number.isFinite(value) || value <= 0 || !input.validity.valid) {
    input.value = String(p[field] ?? '');
    showError('Veuillez saisir une valeur positive valide.');
    return;
  }
  adminSavingProductValues.value = true;
  try {
    const result = await updateProduct(adminRefProductId(p), { [field]: value });
    if (!result?.success) throw new Error(result?.message || 'Enregistrement impossible.');
    await refreshAdminRefsShooting();
    success(`${field === 'prix' ? 'Prix' : 'Poids'} mis à jour pour la référence et ses déclinaisons.`);
  } catch (e: any) {
    input.value = String(p[field] ?? '');
    showError(e?.message || 'Enregistrement impossible.');
  } finally {
    adminSavingProductValues.value = false;
  }
}

async function publishAdminDrafts() {
  const id = adminRefsShootingId.value;
  if (!id || adminPublishingDrafts.value) return;
  adminPublishingDrafts.value = true;
  try {
    const preview = await ManagerShootingService.publishDrafts(id, true);
    if (!preview.count) {
      success('Aucun produit avec photos en brouillon à mettre en ligne.');
      return;
    }
    if (preview.missingWeight) {
      showError(`${preview.missingWeight} produit(s) sans poids : renseignez leur poids avant de publier les ${preview.count} produits concernés.`);
      return;
    }
    if (!window.confirm(`Mettre en ligne les ${preview.count} produits avec photos en brouillon de la demande #${id} ? Toutes les pages sont concernées. Les autres produits resteront inchangés.`)) return;
    const result = await ManagerShootingService.publishDrafts(id);
    await refreshAdminRefsShooting();
    success(`${result.count} produit(s) mis en ligne.`);
  } catch (e: any) {
    showError(e?.message || 'Erreur lors de la mise en ligne.');
  } finally {
    adminPublishingDrafts.value = false;
  }
}

const adminRefsProduits = computed(() => {
  const list = adminRefsPayload.value?.produits;
  return Array.isArray(list) ? list : [];
});

/** Libellé ligne admin : `reference` du produit (souvent distincte par couleur), puis `reference_base`. */
const adminRefLabel = (p: any) => {
  const r = String(p?.reference || p?.reference_base || "").trim();
  return r || "—";
};

/** Texte du commentaire produit (`commentaires`), vide si absent. */
const adminRefCommentaireAffiche = (p: any): string => {
  const raw =
    p?.commentaires != null && String(p.commentaires).trim() !== ""
      ? String(p.commentaires)
      : p?.commentaire != null && String(p.commentaire).trim() !== ""
      ? String(p.commentaire)
      : "";
  const t = raw.trim();
  if (!t) return "";
  return t
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p[^>]*>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

/** Alias : le filtre est appliqué côté serveur (page courante). */
const adminRefsProduitsFiltered = computed(() => adminRefsProduits.value);

const adminUploadDragOverIds = ref<Set<number>>(new Set());
const adminUploadingProductIds = ref<number[]>([]);
const adminCategorySavingProductIds = ref<number[]>([]);

const adminRefProductId = (p: any): number => {
  const id = p?.id_produit ?? p?.id;
  const n = typeof id === "number" ? id : Number(id);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** URLs absolues de toutes les photos (batch `full`), triées / dédupliquées à l’affichage. */
const adminRefsPhotosByProduct = ref<Record<number, string[]>>({});
const adminPhotoCacheBuster = ref<Record<number, number>>({});
const adminPhotoActionState = ref<Record<string, boolean>>({});

const adminSetPhotoActionState = (key: string, loading: boolean) => {
  if (loading) {
    adminPhotoActionState.value = {
      ...adminPhotoActionState.value,
      [key]: true,
    };
  } else {
    const next = { ...adminPhotoActionState.value };
    delete next[key];
    adminPhotoActionState.value = next;
  }
};

const adminIsPhotoActionLoading = (
  productId: number,
  filename: string,
  action: string,
): boolean =>
  Boolean(adminPhotoActionState.value[`${productId}:${filename}:${action}`]);

const adminBumpPhotoCache = (productId: number) => {
  adminPhotoCacheBuster.value = {
    ...adminPhotoCacheBuster.value,
    [productId]: Date.now(),
  };
  // Persiste la version partagée pour que les autres modules
  // (ProductListView, ProductSheetModal, UploadPhotoView) voient la mise à jour.
  bumpPhotoVersion(productId);
};

const adminGetPhotoFilename = (photoUrl: string): string => {
  if (!photoUrl) return "";
  const segment = photoUrl.split("?")[0].split("/").pop() || "";
  return segment;
};

const adminIsMainPhotoUrl = (url: string): boolean => {
  const f = adminGetPhotoFilename(url).toLowerCase();
  return /-c\.(jpg|png)$/i.test(f);
};

const adminGetPhotoPositionFromUrl = (photoUrl: string): string => {
  const key = adminGetPhotoFilename(photoUrl).toLowerCase();
  const m = key.match(/^.+?-(c|z-\d+)\.(jpg|png)$/i);
  return m ? m[1] : "";
};

const adminSortPhotoKeys = (keys: string[]): string[] => {
  return [...keys].sort((a, b) => {
    const mainA = /-c\.(jpg|png)$/i.test(a);
    const mainB = /-c\.(jpg|png)$/i.test(b);
    if (mainA && !mainB) return -1;
    if (!mainA && mainB) return 1;
    if (mainA && mainB) return 0;
    const ma = a.match(/-z-(\d+)\.(jpg|png)$/i);
    const mb = b.match(/-z-(\d+)\.(jpg|png)$/i);
    const na = ma ? Number.parseInt(ma[1], 10) : 0;
    const nb = mb ? Number.parseInt(mb[1], 10) : 0;
    return na - nb;
  });
};

const adminDedupePhotoUrls = (urls: string[]): string[] => {
  const byKey = new Map<string, string>();
  for (const url of urls) {
    const fn = adminGetPhotoFilename(url);
    if (!fn) continue;
    if (!byKey.has(fn)) byKey.set(fn, url);
  }
  const keys = adminSortPhotoKeys(Array.from(byKey.keys()));
  return keys.map((k) => byKey.get(k)).filter((u): u is string => Boolean(u));
};

const adminRefPhotoUrlsForProduct = (p: any): string[] => {
  const id = adminRefProductId(p);
  if (!id) return [];
  return adminDedupePhotoUrls(adminRefsPhotosByProduct.value[id] || []);
};

const adminRefPhotoDisplayUrl = (productId: number, url: string): string => {
  if (!url) return "";
  const liste = toAdminListePreviewUrl(url);
  const b = adminPhotoCacheBuster.value[productId] ?? getPhotoVersion(productId);
  if (!b) return liste;
  const sep = liste.includes("?") ? "&" : "?";
  return `${liste}${sep}v=${b}`;
};

const adminPremelIsDraft = (p: any): boolean => String(p?.premel ?? "") === "1";

const adminProductIsVisible = (p: any): boolean => {
  const v = p?.visible;
  return v === true || v === 1 || v === "1";
};

type AdminProductStatus = "brouillon" | "en_ligne" | "hors_ligne";

const adminProductStatus = (p: any): AdminProductStatus => {
  if (adminPremelIsDraft(p)) return "brouillon";
  if (adminProductIsVisible(p)) return "en_ligne";
  return "hors_ligne";
};

const adminProductStatusLabel = (p: any): string => {
  const status = adminProductStatus(p);
  if (status === "brouillon") return "Brouillon";
  if (status === "en_ligne") return "En ligne";
  return "Hors ligne";
};

const adminProductStatusBadgeClass = (p: any): string => {
  const status = adminProductStatus(p);
  if (status === "brouillon") return "bg-amber-600 text-white";
  if (status === "en_ligne") return "bg-emerald-500 text-white";
  return "bg-gray-400 text-white";
};

const adminPhotoApiBase = (): string => getApiBaseUrl().replace(/\/$/, "");

const adminOriginalProductPhotoUrl = (
  _productId: number,
  photoUrl: string,
): string => getProductOriginalImageUrl(photoUrl);

const toAbsAdminPhotoUrl = (path: string): string => getProductImageUrl(path);

const toAdminListePreviewUrl = (url: string): string => {
  if (!url) return "";
  const [basePart, query = ""] = url.split("?");
  let preview = basePart;
  if (preview.includes("/Produits/original/")) {
    preview = preview.replace("/Produits/original/", "/Produits/liste/");
  } else if (preview.includes("/Produits/zoom/")) {
    preview = preview.replace("/Produits/zoom/", "/Produits/liste/");
  } else if (preview.includes("/original/")) {
    preview = preview.replace("/original/", "/liste/");
  } else if (preview.includes("/zoom/")) {
    preview = preview.replace("/zoom/", "/liste/");
  }
  return query ? `${preview}?${query}` : preview;
};

const loadAdminRefsPhotosForAdminTable = async () => {
  const list = adminRefsPayload.value?.produits;
  const produits = Array.isArray(list) ? list : [];
  const ids = produits
    .map((row: any) => adminRefProductId(row))
    .filter((id: number) => id > 0);
  if (!ids.length) {
    adminRefsPhotosByProduct.value = {};
    return;
  }
  try {
    const { success, photosByProduct } = await getProductPhotosBatch(ids, true);
    if (!success || !photosByProduct) return;
    const next: Record<number, string[]> = {};
    for (const id of ids) {
      const photos = photosByProduct[id];
      if (Array.isArray(photos) && photos.length > 0) {
        next[id] = photos.map((u) => toAbsAdminPhotoUrl(String(u)));
      }
    }
    adminRefsPhotosByProduct.value = next;
  } catch {
    /* ignore */
  }
};

const handleAdminRotatePhoto = async (p: any, photoUrl: string) => {
  const pid = adminRefProductId(p);
  const filename = adminGetPhotoFilename(photoUrl);
  if (!pid || !filename) {
    showError("Impossible de déterminer le fichier à pivoter.");
    return;
  }
  const key = `${pid}:${filename}:rotate`;
  adminSetPhotoActionState(key, true);
  try {
    await rotateProductPhoto(pid, filename, "clockwise");
    adminBumpPhotoCache(pid);
    await loadAdminRefsPhotosForAdminTable();
    success("Rotation enregistrée.");
  } catch (e: any) {
    showError(e?.message || "Erreur lors de la rotation.");
  } finally {
    adminSetPhotoActionState(key, false);
  }
};

const handleAdminSetAsCover = async (p: any, photoUrl: string) => {
  const pid = adminRefProductId(p);
  if (!pid || adminIsMainPhotoUrl(photoUrl)) return;
  const urls = adminRefPhotoUrlsForProduct(p);
  const currentPositions = urls
    .map(adminGetPhotoPositionFromUrl)
    .filter(Boolean) as string[];
  const selectedPosition = adminGetPhotoPositionFromUrl(photoUrl);
  const coverPosition = "c";
  if (!selectedPosition || !currentPositions.length) {
    showError("Impossible de modifier la photo de couverture.");
    return;
  }
  const sortedPositions = [...currentPositions].sort((a, b) => {
    if (a === "c") return -1;
    if (b === "c") return 1;
    const numA = parseInt(a.replace("z-", ""), 10) || 0;
    const numB = parseInt(b.replace("z-", ""), 10) || 0;
    return numA - numB;
  });
  const positionsInNewOrder = sortedPositions.map((pos) => {
    if (pos === coverPosition) return selectedPosition;
    if (pos === selectedPosition) return coverPosition;
    return pos;
  });
  const filename = adminGetPhotoFilename(photoUrl);
  const key = `${pid}:${filename}:setCover`;
  adminSetPhotoActionState(key, true);
  try {
    await reorderProductPhotos(pid, positionsInNewOrder);
    adminBumpPhotoCache(pid);
    await loadAdminRefsPhotosForAdminTable();
    success("Photo de couverture enregistrée.");
  } catch (e: any) {
    showError(e?.message || "Erreur lors du changement de couverture.");
  } finally {
    adminSetPhotoActionState(key, false);
  }
};

// Drag-and-drop pour réorganiser les photos (admin)
const adminDraggingPhoto = ref<{ productId: number; index: number } | null>(
  null,
);
const adminDragOverPhoto = ref<{ productId: number; index: number } | null>(
  null,
);
const adminReorderingProducts = ref<Set<number>>(new Set());

const isAdminDraggingPhoto = (productId: number, index: number): boolean =>
  adminDraggingPhoto.value?.productId === productId &&
  adminDraggingPhoto.value?.index === index;

const isAdminDragOverPhoto = (productId: number, index: number): boolean =>
  adminDragOverPhoto.value?.productId === productId &&
  adminDragOverPhoto.value?.index === index;

const isAdminReorderingPhotos = (productId: number): boolean =>
  adminReorderingProducts.value.has(productId);

const handleAdminPhotoDragStart = (
  event: DragEvent,
  p: any,
  index: number,
) => {
  const pid = adminRefProductId(p);
  if (!pid || adminReorderingProducts.value.has(pid)) {
    event.preventDefault();
    return;
  }
  adminDraggingPhoto.value = { productId: pid, index };
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", "");
  }
};

const handleAdminPhotoDragEnd = () => {
  adminDraggingPhoto.value = null;
  adminDragOverPhoto.value = null;
};

const handleAdminPhotoItemDragOver = (
  event: DragEvent,
  productId: number,
  index: number,
) => {
  event.preventDefault();
  event.stopPropagation();
  if (
    adminDraggingPhoto.value &&
    adminDraggingPhoto.value.productId === productId
  ) {
    adminDragOverPhoto.value = { productId, index };
  }
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = "move";
  }
};

const handleAdminPhotoItemDragLeave = () => {
  // La réinitialisation est gérée au drop / dragend
};

const handleAdminPhotoDropOnItem = async (
  event: DragEvent,
  p: any,
  dropIndex: number,
) => {
  event.preventDefault();
  event.stopPropagation();
  const pid = adminRefProductId(p);
  if (
    !pid ||
    !adminDraggingPhoto.value ||
    adminDraggingPhoto.value.productId !== pid
  ) {
    adminDraggingPhoto.value = null;
    adminDragOverPhoto.value = null;
    return;
  }
  const dragIndex = adminDraggingPhoto.value.index;
  if (dragIndex === dropIndex) {
    adminDraggingPhoto.value = null;
    adminDragOverPhoto.value = null;
    return;
  }
  await performAdminPhotoReorder(p, dragIndex, dropIndex);
};

const performAdminPhotoReorder = async (
  p: any,
  dragIndex: number,
  dropIndex: number,
) => {
  const pid = adminRefProductId(p);
  const urls = adminRefPhotoUrlsForProduct(p);
  if (!pid || urls.length === 0) {
    adminDraggingPhoto.value = null;
    adminDragOverPhoto.value = null;
    return;
  }

  const newOrder = [...urls];
  const [moved] = newOrder.splice(dragIndex, 1);
  newOrder.splice(dropIndex, 0, moved);

  const currentPositions = urls.map(adminGetPhotoPositionFromUrl);
  const positionsInNewOrder: string[] = [];
  for (let i = 0; i < newOrder.length; i++) {
    const photoUrl = newOrder[i];
    const oldIndex = urls.indexOf(photoUrl);
    if (oldIndex !== -1 && currentPositions[oldIndex]) {
      positionsInNewOrder.push(currentPositions[oldIndex]);
    } else {
      positionsInNewOrder.push(i === 0 ? "c" : `z-${i}`);
    }
  }

  adminDraggingPhoto.value = null;
  adminDragOverPhoto.value = null;

  const nextReordering = new Set(adminReorderingProducts.value);
  nextReordering.add(pid);
  adminReorderingProducts.value = nextReordering;
  try {
    await reorderProductPhotos(pid, positionsInNewOrder);
    adminBumpPhotoCache(pid);
    await loadAdminRefsPhotosForAdminTable();
    success("Ordre des photos enregistré.");
  } catch (e: any) {
    showError(e?.message || "Erreur lors de la réorganisation des photos.");
  } finally {
    const after = new Set(adminReorderingProducts.value);
    after.delete(pid);
    adminReorderingProducts.value = after;
  }
};

const handleAdminDeletePhoto = async (p: any, photoUrl: string) => {
  const pid = adminRefProductId(p);
  const filename = adminGetPhotoFilename(photoUrl);
  if (!pid || !filename) {
    showError("Impossible de déterminer le fichier à supprimer.");
    return;
  }
  if (adminIsMainPhotoUrl(photoUrl) && !adminPremelIsDraft(p)) {
    showError("Impossible de supprimer la photo de couverture.");
    return;
  }
  if (
    !window.confirm(`Supprimer la photo « ${filename} » pour cette référence ?`)
  ) {
    return;
  }
  const key = `${pid}:${filename}:delete`;
  adminSetPhotoActionState(key, true);
  try {
    await deleteProductPhoto(pid, filename);
    adminBumpPhotoCache(pid);
    await loadAdminRefsPhotosForAdminTable();
    success("Photo supprimée.");
  } catch (e: any) {
    showError(e?.message || "Erreur lors de la suppression.");
  } finally {
    adminSetPhotoActionState(key, false);
  }
};

const setAdminDragOver = (id: number, active: boolean) => {
  const s = new Set(adminUploadDragOverIds.value);
  if (active) s.add(id);
  else s.delete(id);
  adminUploadDragOverIds.value = s;
};

const isAdminUploadDragOver = (id: number) =>
  id > 0 && adminUploadDragOverIds.value.has(id);

const isAdminUploading = (id: number) =>
  id > 0 && adminUploadingProductIds.value.includes(id);

const adminUploadDragEnter = (e: DragEvent, id: number) => {
  e.preventDefault();
  e.stopPropagation();
  if (e.dataTransfer?.types?.includes("Files")) {
    setAdminDragOver(id, true);
  }
};

const adminUploadDragOver = (e: DragEvent, id: number) => {
  e.preventDefault();
  e.stopPropagation();
  if (e.dataTransfer?.types?.includes("Files")) {
    e.dataTransfer.dropEffect = "copy";
    setAdminDragOver(id, true);
  }
};

const adminUploadDragLeave = (e: DragEvent, id: number) => {
  e.preventDefault();
  e.stopPropagation();
  const rel = e.relatedTarget as HTMLElement | null;
  const cur = e.currentTarget as HTMLElement | null;
  if (!cur?.contains(rel)) {
    setAdminDragOver(id, false);
  }
};

const adminUploadDrop = async (e: DragEvent, p: any) => {
  e.preventDefault();
  e.stopPropagation();
  const pid = adminRefProductId(p);
  setAdminDragOver(pid, false);
  const files = e.dataTransfer?.files;
  if (files?.length) {
    await runAdminRefPhotosUpload(p, files);
  }
};

const triggerAdminRefPhotoDialog = (p: any) => {
  const pid = adminRefProductId(p);
  if (!pid) return;
  const input = document.createElement("input");
  input.type = "file";
  input.accept = productPhotoInputAccept;
  input.multiple = true;
  input.addEventListener("change", (ev) => {
    const t = ev.target as HTMLInputElement;
    if (t.files?.length) {
      void runAdminRefPhotosUpload(p, t.files);
    }
    t.value = "";
    input.remove();
  });
  input.click();
};

const triggerAdminBulkNamedPhotosPicker = () => {
  if (isAdminBulkNamedUploading.value) return;
  adminBulkNamedPhotosInputRef.value?.click();
};

const onAdminBulkNamedDropzoneClick = () => {
  triggerAdminBulkNamedPhotosPicker();
};

const onAdminBulkNamedDragEnter = (e: DragEvent) => {
  e.preventDefault();
  if (isAdminBulkNamedUploading.value) return;
  isAdminBulkNamedDragOver.value = true;
};

const onAdminBulkNamedDragOver = (e: DragEvent) => {
  e.preventDefault();
  if (isAdminBulkNamedUploading.value) return;
  if (e.dataTransfer) {
    e.dataTransfer.dropEffect = "copy";
  }
  isAdminBulkNamedDragOver.value = true;
};

const onAdminBulkNamedDragLeave = (e: DragEvent) => {
  e.preventDefault();
  const rel = e.relatedTarget as Node | null;
  const cur = e.currentTarget as HTMLElement | null;
  if (!cur?.contains(rel)) {
    isAdminBulkNamedDragOver.value = false;
  }
};

const onAdminBulkNamedDrop = async (e: DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  isAdminBulkNamedDragOver.value = false;
  if (isAdminBulkNamedUploading.value) return;
  const items = e.dataTransfer?.items;
  if (items?.length) {
    const hasDirectory = Array.from(items).some((item) => {
      if (item.kind !== "file") return false;
      const entry =
        typeof item.webkitGetAsEntry === "function"
          ? item.webkitGetAsEntry()
          : null;
      return Boolean(entry && entry.isDirectory);
    });
    if (hasDirectory) {
      showError(
        "Les dossiers ne sont pas pris en charge ici. Déposez uniquement des fichiers image.",
      );
      return;
    }
  }
  const files = e.dataTransfer?.files;
  if (files?.length) {
    await runAdminBulkNamedPhotosUpload(Array.from(files));
  }
};

const onAdminBulkNamedPhotosInputChange = (e: Event) => {
  const input = e.target as HTMLInputElement;
  const list = input.files;
  input.value = "";
  if (list?.length) {
    void runAdminBulkNamedPhotosUpload(Array.from(list));
  }
};

/** Partie nom fichier sans chemin ni extension. */
const adminPhotoFileStem = (file: File): string => {
  const raw = (file.name || "").replace(/^.*[/\\]/, "").trim();
  return raw.replace(/\.[^.]+$/i, "").trim();
};

const adminNorm = (s: unknown): string =>
  String(s ?? "")
    .trim()
    .toLowerCase();
const adminNormLoose = (s: unknown): string =>
  adminNorm(s)
    .replace(/[\\/:_\s]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

/** Aligné sur le ZIP backend : `^(.+)-(\\d+)$` puis index 0..19. */
const ADMIN_BULK_NAMED_MAX_PHOTO_INDEX = 19;

const adminParseZipStylePhotoStem = (
  stem: string,
): { refKey: string; index: number; isStrictPosition: boolean } | null => {
  const s = stem.trim();
  const m = s.match(/^(.+)-(\d+)$/);
  if (!m) return null;
  const idx = Number.parseInt(m[2], 10);
  if (!Number.isFinite(idx) || idx < 0) {
    return null;
  }
  return {
    refKey: m[1],
    index: idx,
    isStrictPosition: idx <= ADMIN_BULK_NAMED_MAX_PHOTO_INDEX,
  };
};

/**
 * Associe une clé de référence (sans suffixe -position) à un produit de la demande.
 * Ordre : référence exacte → référence de base exacte → un seul produit dont la référence commence par « key- » ou « key_ ».
 */
const adminResolveProductIdFromRefKey = (
  refKey: string,
  produits: any[],
): number | null => {
  const k = refKey.trim().toLowerCase();
  const kLoose = adminNormLoose(refKey);
  if (!k) return null;

  const exactRef = produits.filter((p) => {
    const ref = adminNorm(p?.reference);
    return ref === k || adminNormLoose(p?.reference) === kLoose;
  });
  if (exactRef.length === 1) return adminRefProductId(exactRef[0]);
  if (exactRef.length > 1) return null;

  const exactBase = produits.filter((p) => {
    const base = adminNorm(p?.reference_base);
    return base === k || adminNormLoose(p?.reference_base) === kLoose;
  });
  if (exactBase.length === 1) return adminRefProductId(exactBase[0]);
  if (exactBase.length > 1) return null;

  const byPrefix = produits.filter((p) => {
    const r = adminNorm(p?.reference);
    const rLoose = adminNormLoose(p?.reference);
    return (
      (r && (r.startsWith(`${k}-`) || r.startsWith(`${k}_`))) ||
      (rLoose && rLoose.startsWith(`${kLoose}-`))
    );
  });
  if (byPrefix.length === 1) return adminRefProductId(byPrefix[0]);
  return null;
};

/**
 * Résout stem → produit + ordre d’envoi. Convention ZIP d’abord : `{ref}-{0..19}` ;
 * sinon même logique qu’avant sur le stem entier (sortIndex 0).
 */
const adminResolveProductIdFromBulkPhotoStem = (
  stem: string,
  produits: any[],
): {
  productId: number;
  sortIndex: number;
  fromZipConvention: boolean;
} | null => {
  const parsed = adminParseZipStylePhotoStem(stem);
  if (parsed) {
    const pid = adminResolveProductIdFromRefKey(parsed.refKey, produits);
    if (pid != null) {
      // Admin uniquement: un suffixe numérique > 19 est traité comme numéro Lightroom.
      // On garde un ordre d'envoi stable par numéro, sans l'interpréter comme position stricte ZIP.
      if (!parsed.isStrictPosition && !authStore.isImpersonating) {
        return null;
      }
      return {
        productId: pid,
        sortIndex: parsed.index,
        fromZipConvention: parsed.isStrictPosition,
      };
    }
    // Fallback admin-only: si la clé complète (ex: "test-robe") ne matche pas,
    // retenter avec le préfixe avant le premier tiret (ex: "test").
    if (authStore.isImpersonating) {
      const firstDash = parsed.refKey.indexOf("-");
      if (firstDash > 0) {
        const head = parsed.refKey.slice(0, firstDash).trim();
        if (head) {
          const headPid = adminResolveProductIdFromRefKey(head, produits);
          if (headPid != null) {
            return {
              productId: headPid,
              sortIndex: parsed.index,
              fromZipConvention: parsed.isStrictPosition,
            };
          }
        }
      }
    }
  }
  const pid = adminResolveProductIdFromRefKey(stem, produits);
  if (pid != null) {
    return { productId: pid, sortIndex: 0, fromZipConvention: false };
  }
  return null;
};

const runAdminBulkNamedPhotosUpload = async (files: File[]) => {
  if (isAdminBulkNamedUploading.value || !files.length) return;
  adminBulkNamedUploadErrors.value = [];
  adminBulkNamedUploadBanner.value = "";

  const valid = files.filter(
    (f) => isSupportedProductPhotoFormat(f) && isSupportedProductPhotoSize(f),
  );
  if (!valid.length) {
    showError(getUnsupportedProductPhotoErrorMessage());
    return;
  }
  if (valid.length !== files.length) {
    showError(
      "Certains fichiers ont été ignorés (format ou taille max 15 Mo).",
    );
  }

  let produits = adminRefsProduits.value;
  const sid = adminRefsShootingId.value;
  if (sid != null && adminRefsTotal.value > produits.length) {
    try {
      const allResp = await getShootingForEdit(sid, {
        flatProduits: true,
        page: 1,
        limit: Math.min(Math.max(adminRefsTotal.value, 1), 5000),
        q: adminRefsReferenceFilter.value.trim() || undefined,
      });
      if (Array.isArray(allResp?.produits)) {
        produits = allResp.produits;
      }
    } catch {
      /* fallback page courante */
    }
  }
  if (!produits.length) {
    showError("Aucune référence chargée pour cette demande.");
    return;
  }

  const byProduct = new Map<number, { file: File; sortIndex: number }[]>();
  const errors: string[] = [];
  const seenZipSlot = new Set<string>();

  for (const f of valid) {
    const stem = adminPhotoFileStem(f);
    const resolved = adminResolveProductIdFromBulkPhotoStem(stem, produits);
    if (!resolved) {
      errors.push(
        `"${f.name}" : aucune référence unique ne correspond au nom « ${stem} » dans cette demande.`,
      );
      continue;
    }
    if (resolved.fromZipConvention) {
      const slotKey = `${resolved.productId}:${resolved.sortIndex}`;
      if (seenZipSlot.has(slotKey)) {
        errors.push(
          `"${f.name}" : doublon du numéro ${resolved.sortIndex} pour le même produit (même convention que le ZIP).`,
        );
        continue;
      }
      seenZipSlot.add(slotKey);
    }
    const bucket = byProduct.get(resolved.productId);
    const entry = { file: f, sortIndex: resolved.sortIndex };
    if (bucket) bucket.push(entry);
    else byProduct.set(resolved.productId, [entry]);
  }

  if (byProduct.size === 0) {
    adminBulkNamedUploadErrors.value = [...new Set(errors)];
    adminBulkNamedUploadBanner.value =
      "Aucune photo importée. Renommez les fichiers comme les références de la demande.";
    return;
  }

  isAdminBulkNamedUploading.value = true;
  let okCount = 0;
  try {
    for (const [pid, entries] of byProduct) {
      try {
        const sorted = [...entries].sort((a, b) => {
          if (a.sortIndex !== b.sortIndex) return a.sortIndex - b.sortIndex;
          return a.file.name.localeCompare(b.file.name, undefined, {
            numeric: true,
          });
        });
        const files = sorted.map((e) => e.file);
        await uploadProductPhotosBatched(pid, files);
        okCount++;
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Erreur d’upload";
        errors.push(`Produit #${pid} : ${msg}`);
      }
    }

    if (okCount > 0) {
      success(
        okCount === 1
          ? "Photos importées pour une référence."
          : `Photos importées pour ${okCount} référence(s).`,
      );
      await refreshAdminRefsShooting();
    }

    adminBulkNamedUploadErrors.value = [...new Set(errors)];
    if (errors.length) {
      adminBulkNamedUploadBanner.value =
        okCount > 0
          ? "Certaines photos n’ont pas pu être importées (voir le détail)."
          : "Aucune photo importée. Vérifiez les noms de fichiers.";
    }
  } catch (err: unknown) {
    showError(
      err instanceof Error ? err.message : "Erreur lors de l’envoi des photos.",
    );
  } finally {
    isAdminBulkNamedUploading.value = false;
  }
};

const runAdminRefPhotosUpload = async (
  p: any,
  filesInput: FileList | File[],
) => {
  const pid = adminRefProductId(p);
  if (!pid) {
    showError("Produit invalide.");
    return;
  }
  const arr = Array.isArray(filesInput)
    ? filesInput
    : Array.from(filesInput ?? []);
  if (!arr.length) return;

  const valid = arr.filter(
    (f) => isSupportedProductPhotoFormat(f) && isSupportedProductPhotoSize(f),
  );
  if (!valid.length) {
    showError(getUnsupportedProductPhotoErrorMessage());
    return;
  }
  if (valid.length !== arr.length) {
    showError("Certaines photos ont été ignorées (format ou taille).");
  }

  adminUploadingProductIds.value = [...adminUploadingProductIds.value, pid];
  try {
    const response = await uploadProductPhotosBatched(pid, valid);
    if (response?.success) {
      success(response.message || "Photos uploadées avec succès.");
      await refreshAdminRefsShooting();
    } else {
      showError(response?.message || "Erreur lors de l’upload.");
    }
  } catch (err: any) {
    showError(err?.message || "Erreur lors de l’upload.");
  } finally {
    adminUploadingProductIds.value = adminUploadingProductIds.value.filter(
      (x) => x !== pid,
    );
  }
};

const applyAdminRefsPaginationMeta = (response: any) => {
  const pag = response?.pagination;
  if (pag) {
    adminRefsPage.value = Number(pag.page) || adminRefsPage.value;
    adminRefsLimit.value = Number(pag.limit) || adminRefsLimit.value;
    adminRefsTotal.value = Number(pag.total) || 0;
  } else {
    adminRefsTotal.value = Array.isArray(response?.produits)
      ? response.produits.length
      : 0;
  }
};

let adminRefsFetchSeq = 0;

const fetchAdminRefsPage = async (
  shootingId: number,
  opts?: { silent?: boolean },
) => {
  const seq = ++adminRefsFetchSeq;
  const response = await getShootingForEdit(shootingId, {
    flatProduits: true,
    page: adminRefsPage.value,
    limit: adminRefsLimit.value,
    q: adminRefsReferenceFilter.value.trim() || undefined,
  });
  // Ignorer une réponse obsolète si une requête plus récente a démarré
  if (seq !== adminRefsFetchSeq) return false;
  if (response?.success && response.shooting) {
    adminRefsPayload.value = response;
    applyAdminRefsPaginationMeta(response);
    void loadAdminRefsPhotosForAdminTable();
    return true;
  }
  if (!opts?.silent) {
    showError(response?.message || "Impossible de charger ce shooting.");
  }
  return false;
};

const refreshAdminRefsShooting = async () => {
  const sid = adminRefsShootingId.value;
  if (sid == null) return;
  try {
    await ensureCategoriesTreeForAdmin();
    await fetchAdminRefsPage(sid, { silent: true });
  } catch {
    /* ignore */
  }
};

const handleAdminRefsPageChange = async (page: number) => {
  if (page === adminRefsPage.value) return;
  adminRefsPage.value = page;
  const sid = adminRefsShootingId.value;
  if (sid == null) return;
  adminRefsLoading.value = true;
  try {
    await fetchAdminRefsPage(sid);
  } catch (err: any) {
    showError(err?.message || "Erreur lors du chargement des références.");
  } finally {
    adminRefsLoading.value = false;
  }
};

const handleAdminRefsLimitChange = async (limit: number) => {
  adminRefsLimit.value = limit;
  adminRefsPage.value = 1;
  const sid = adminRefsShootingId.value;
  if (sid == null) return;
  adminRefsLoading.value = true;
  try {
    await fetchAdminRefsPage(sid);
  } catch (err: any) {
    showError(err?.message || "Erreur lors du chargement des références.");
  } finally {
    adminRefsLoading.value = false;
  }
};

watch(adminRefsReferenceFilter, () => {
  if (adminRefsIgnoreFilterWatch) return;
  if (adminRefsShootingId.value == null) return;
  if (adminRefsFilterDebounce) clearTimeout(adminRefsFilterDebounce);
  adminRefsFilterDebounce = setTimeout(async () => {
    adminRefsPage.value = 1;
    const sid = adminRefsShootingId.value;
    if (sid == null) return;
    adminRefsLoading.value = true;
    try {
      await fetchAdminRefsPage(sid);
    } catch (err: any) {
      showError(err?.message || "Erreur lors du filtrage des références.");
    } finally {
      adminRefsLoading.value = false;
    }
  }, 300);
});

const openAdminRefsFocus = async (shootingId: number) => {
  adminRefsIgnoreFilterWatch = true;
  adminRefsShootingId.value = shootingId;
  adminRefsPayload.value = null;
  adminRefsLoading.value = true;
  adminRefsReferenceFilter.value = "";
  adminRefsPage.value = 1;
  adminRefsTotal.value = 0;
  adminBulkNamedUploadErrors.value = [];
  adminBulkNamedUploadBanner.value = "";
  isAdminBulkNamedDragOver.value = false;
  adminUploadDragOverIds.value = new Set();
  adminUploadingProductIds.value = [];
  adminCategorySavingProductIds.value = [];
  adminPhotoCacheBuster.value = {};
  adminPhotoActionState.value = {};
  try {
    await ensureCategoriesTreeForAdmin();
    const ok = await fetchAdminRefsPage(shootingId);
    if (!ok) closeAdminRefsFocus();
  } catch (err: any) {
    showError(err?.message || "Erreur lors du chargement du shooting.");
    closeAdminRefsFocus();
  } finally {
    adminRefsIgnoreFilterWatch = false;
    adminRefsLoading.value = false;
  }
};

const closeAdminRefsFocus = () => {
  adminRefsIgnoreFilterWatch = true;
  if (adminRefsFilterDebounce) {
    clearTimeout(adminRefsFilterDebounce);
    adminRefsFilterDebounce = null;
  }
  adminRefsShootingId.value = null;
  adminRefsPayload.value = null;
  adminRefsLoading.value = false;
  adminRefsReferenceFilter.value = "";
  adminRefsPage.value = 1;
  adminRefsTotal.value = 0;
  adminBulkNamedUploadErrors.value = [];
  adminBulkNamedUploadBanner.value = "";
  isAdminBulkNamedDragOver.value = false;
  isAdminBulkNamedUploading.value = false;
  adminUploadDragOverIds.value = new Set();
  adminUploadingProductIds.value = [];
  adminCategorySavingProductIds.value = [];
  adminRefsPhotosByProduct.value = {};
  adminPhotoCacheBuster.value = {};
  adminPhotoActionState.value = {};
  adminRefsIgnoreFilterWatch = false;
};

// Modal d'édition
const showEditModal = ref(false);
const showStockModal = ref(false);
const editingShootingData = ref<any>(null);
const isLoadingEditData = ref(false);

// Édition inline de produit
const editingProductId = ref<number | null>(null);
const productFormData = ref<any>({});
const isLoadingProductData = ref(false);
const isSavingProduct = ref(false);
const isTranslatingListMel = ref(false);
const descriptionInputListMel = ref("");

const EMOJI_REGEX =
  /([\u{1F000}-\u{1FAFF}]|[\u{2600}-\u{27BF}]|[\u{2300}-\u{23FF}]|[\u{2B00}-\u{2BFF}]|\u200D|\uFE0F)/u;

const containsEmoji = (text?: string | null): boolean =>
  !!text && EMOJI_REGEX.test(text);

const LANG_MAP_LIST: { key: string; lang: string }[] = [
  { key: "descriptionFr", lang: "francais" },
  { key: "descriptionEn", lang: "anglais" },
  { key: "descriptionIt", lang: "italien" },
  { key: "descriptionEs", lang: "espagnol" },
  { key: "descriptionDe", lang: "allemand" },
];

async function autoTranslateListMel() {
  const text = descriptionInputListMel.value?.trim();
  if (!text) {
    showError("Veuillez saisir une description.");
    return;
  }
  if (containsEmoji(text)) {
    showError(
      "La description ne doit pas contenir d'emojis : ils ne s'affichent pas correctement sur la fiche produit et perturbent la traduction automatique.",
    );
    return;
  }
  isTranslatingListMel.value = true;
  try {
    const API_URL = getApiBaseUrl();
    const detectRes = await fetchRest(`${API_URL}/translate/detect`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    });
    const detectData = await detectRes.json();
    const detectedLang = detectData.language || "francais";

    const targetLangs = LANG_MAP_LIST
      .filter((e) => e.lang !== detectedLang)
      .map((e) => e.lang);

    const response = await fetchRest(`${API_URL}/translate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, sourceLang: detectedLang, targetLangs }),
    });
    if (!response.ok) throw new Error("Translation failed");
    const data = await response.json();

    for (const entry of LANG_MAP_LIST) {
      if (entry.lang === detectedLang) {
        productFormData.value[entry.key] = text;
      } else if (data[entry.lang]) {
        productFormData.value[entry.key] = data[entry.lang];
      }
    }
  } catch (err) {
    console.error("Translation error:", err);
    showError("Erreur lors de la traduction. Veuillez réessayer.");
  } finally {
    isTranslatingListMel.value = false;
  }
}

// Données de référence
const marques = ref<any[]>([]);
const collections = ref<any[]>([]);
const provenances = ref<any[]>([]);
const declinaisons = ref<any[]>([]);
const packs = ref<any[]>([]);
const couleurs = ref<any[]>([]);
const compositions = ref<
  Array<{
    id: number;
    libelle: string;
    id_categorie: string | null;
    type: "composition" | "localisation";
  }>
>([]);
const caracteristiques = ref<any[]>([]);
const caracteristiquesCollapsed = ref(true);
const categoriesGenerales = ref<any[]>([]);
const categoriesAll = ref<any[]>([]);
// Arbre des catégories (structure identique à ProductSheetModal)
const categoriesTree = ref<CategorieNode[]>([]);

/** Charge l’arbre GraphQL (selects catégories, vue admin références). */
const ensureCategoriesTreeForAdmin = async () => {
  if (categoriesTree.value.length > 0) return;
  try {
    categoriesTree.value = await getCategoriesTree("fr");
  } catch {
    /* ignore */
  }
};

/** Parse `id_categorie` numérique (2 / 4 / 6 chiffres) pour les selects admin. */
const adminParseIdCategorie = (
  idCategorie: string | number | null | undefined,
): { gen: string; sous: string; leaf: string } => {
  if (idCategorie == null || idCategorie === "") {
    return { gen: "", sous: "", leaf: "" };
  }
  const s = String(idCategorie).trim();
  if (!/^\d+$/.test(s)) {
    return { gen: "", sous: "", leaf: "" };
  }
  if (s.length === 2) return { gen: s, sous: "", leaf: "" };
  if (s.length === 4) return { gen: s.substring(0, 2), sous: s, leaf: "" };
  return { gen: s.substring(0, 2), sous: s.substring(0, 4), leaf: s };
};

const adminRowCatParts = (p: any) => adminParseIdCategorie(p?.id_categorie);

const adminRootCategorySelectOptions = computed(() =>
  categoriesTree.value.map((c) => ({
    label: c.label,
    value: String(c.id_categorie),
  })),
);

const adminSousCategorySelectOptions = (gen: string) => {
  if (!gen || !categoriesTree.value.length) return [];
  const n = categoriesTree.value.find((c) => String(c.id_categorie) === gen);
  return (n?.children || []).map((c) => ({
    label: c.label,
    value: String(c.id_categorie),
  }));
};

const adminSousSousCategorySelectOptions = (gen: string, sous: string) => {
  if (!gen || !sous || !categoriesTree.value.length) return [];
  const n1 = categoriesTree.value.find((c) => String(c.id_categorie) === gen);
  const n2 = n1?.children?.find((c) => String(c.id_categorie) === sous);
  return (n2?.children || []).map((c) => ({
    label: c.label,
    value: String(c.id_categorie),
  }));
};

const isAdminSavingCategory = (id: number) =>
  id > 0 && adminCategorySavingProductIds.value.includes(id);

async function saveAdminRowIdCategorie(p: any, idCategorie: number | null) {
  const pid = adminRefProductId(p);
  if (!pid) return;
  if (adminCategorySavingProductIds.value.includes(pid)) return;
  adminCategorySavingProductIds.value = [
    ...adminCategorySavingProductIds.value,
    pid,
  ];
  try {
    const res = await updateProduct(pid, {
      idCategorie: idCategorie != null && idCategorie > 0 ? idCategorie : null,
    });
    if (res?.success) {
      success("Catégorie mise à jour.");
      await refreshAdminRefsShooting();
    } else {
      showError(res?.message || "Erreur lors de l’enregistrement.");
    }
  } catch (e: any) {
    showError(e?.message || "Erreur lors de l’enregistrement.");
  } finally {
    adminCategorySavingProductIds.value =
      adminCategorySavingProductIds.value.filter((x) => x !== pid);
  }
}

const onAdminCatGenChange = (p: any, ev: Event) => {
  const v = (ev.target as HTMLSelectElement).value;
  const id = v ? parseInt(v, 10) : null;
  void saveAdminRowIdCategorie(p, id != null && !Number.isNaN(id) ? id : null);
};

const onAdminCatSousChange = (p: any, ev: Event) => {
  const v = (ev.target as HTMLSelectElement).value;
  const parts = adminParseIdCategorie(p.id_categorie);
  const gen = parts.gen;
  if (!v) {
    const gid = gen ? parseInt(gen, 10) : null;
    void saveAdminRowIdCategorie(
      p,
      gid != null && !Number.isNaN(gid) ? gid : null,
    );
    return;
  }
  const sid = parseInt(v, 10);
  void saveAdminRowIdCategorie(p, Number.isNaN(sid) ? null : sid);
};

const onAdminCatSousSousChange = (p: any, ev: Event) => {
  const v = (ev.target as HTMLSelectElement).value;
  const parts = adminParseIdCategorie(p.id_categorie);
  if (!v) {
    const sous = parts.sous;
    const gen = parts.gen;
    const fallback = sous ? parseInt(sous, 10) : gen ? parseInt(gen, 10) : null;
    void saveAdminRowIdCategorie(
      p,
      fallback != null && !Number.isNaN(fallback) ? fallback : null,
    );
    return;
  }
  const lid = parseInt(v, 10);
  void saveAdminRowIdCategorie(p, Number.isNaN(lid) ? null : lid);
};

// Filtres
const filters = reactive<MelFilters>({
  idShooting: "",
  reference: "",
  confirmation: "",
  dateDemandeDebut: "",
  dateDemandeFin: "",
  dateShootingDebut: "",
  dateShootingFin: "",
});

// Variables pour le formulaire d'édition
const todayDate = new Date().toISOString().split("T")[0];
const compositionAutocompleteOptions = ref<
  Array<{ id: number; label: string }>
>([]);
const showCompositionDropdown = ref(false);
const isLoadingCompositionAutocomplete = ref(false);

// Options computed pour les selects
const marqueOptions = computed(() =>
  marques.value.map((m) => ({ label: m.label, value: m.value })),
);
const categorieOptions = computed(() =>
  categoriesGenerales.value.map((c) => ({
    label: c.label,
    value: c.id.toString(),
  })),
);
const sousCategorieOptions = computed(() => {
  // Utiliser categoriesTree (structure identique à ProductSheetModal)
  const id1 = productFormData.value.idCategorieGenerale;
  if (!id1) return [];
  const found = categoriesTree.value.find(
    (c: any) => String(c.id_categorie) === String(id1),
  );
  if (!found || !found.children) return [];
  return found.children.map((c: any) => ({
    label: c.label,
    value: String(c.id_categorie),
  }));
});
const sousSousCategorieOptions = computed(() => {
  // Utiliser categoriesTree (structure identique à ProductSheetModal)
  const id1 = productFormData.value.idCategorieGenerale;
  const id2 = productFormData.value.idSousCategorie;
  if (!id1 || !id2) return [];
  const level1 = categoriesTree.value.find(
    (c: any) => String(c.id_categorie) === String(id1),
  );
  if (!level1 || !level1.children) return [];
  const level2 = level1.children.find(
    (c: any) => String(c.id_categorie) === String(id2),
  );
  if (!level2 || !level2.children) return [];
  return level2.children.map((c: any) => ({
    label: c.label,
    value: String(c.id_categorie),
  }));
});
const isProductShoe = computed(() =>
  isShoeCategory(
    productFormData.value.idCategorie ??
      productFormData.value.idSousCategorie ??
      productFormData.value.idCategorieGenerale,
  ),
);
const collectionOptions = computed(() =>
  collections.value.map((c) => ({ label: c.label, value: c.id.toString() })),
);
const paysOptions = computed(() =>
  provenances.value.map((p) => ({ label: p.label, value: p.id.toString() })),
);
const tailleOptions = computed(() =>
  declinaisons.value.map((d) => ({
    label: formatDeclinaisonLabel(d.label, d.tailles),
    value: d.id,
  })),
);
const quantiteOptions = computed(() => {
  // Si aucune déclinaison n'est sélectionnée, afficher une liste vide
  if (!productFormData.value.idDeclinaison) {
    return [];
  }

  if (!declinaisons.value || declinaisons.value.length === 0) {
    return [];
  }

  if (!packs.value || packs.value.length === 0) {
    return [];
  }

  // Trouver la déclinaison sélectionnée pour connaître le nombre de tailles
  const selectedDeclinaison = declinaisons.value.find(
    (d) =>
      d && d.id && String(d.id) === String(productFormData.value.idDeclinaison),
  );
  if (!selectedDeclinaison || !selectedDeclinaison.tailles) {
    return [];
  }

  // La longueur correspond à la dernière colonne renseignée : "S,,L" = 3.
  const tailleCount = getDeclinaisonLength(selectedDeclinaison.tailles);

  // Filtrer les packs qui ont le même nombre d'éléments
  const filteredPacks = packs.value.filter((pack: any) => {
    return (
      pack &&
      getPackLength(pack.value) === tailleCount
    );
  });

  return filteredPacks.map((p: any) => ({
    label: p.value ? `${p.label} (${p.value})` : p.label,
    value: p.id,
  }));
});

// Liste des tailles pour les couleurs mélangées (extrait de la déclinaison sélectionnée)
const taillesListForMelangeesEdit = computed(() => {
  if (
    !productFormData.value.venduPar ||
    productFormData.value.venduPar !== "melangees" ||
    !productFormData.value.idDeclinaison
  ) {
    return [];
  }

  const selectedDeclinaison = declinaisons.value.find(
    (d) =>
      d && d.id && String(d.id) === String(productFormData.value.idDeclinaison),
  );
  if (!selectedDeclinaison || !selectedDeclinaison.tailles) {
    return [];
  }

  // Extraire les tailles depuis la chaîne (ex: "S,M,L" -> ['S', 'M', 'L'])
  return selectedDeclinaison.tailles
    .split(",")
    .map((t: string) => t.trim())
    .filter((t: string) => t.length > 0);
});
const venduParOptions = [
  { label: "À la couleur", value: "couleurs" },
  { label: "Couleurs mélangées", value: "melangees" },
  { label: "Tailles aux choix", value: "tailles" },
];

const startIndex = computed(
  () => (currentPage.value - 1) * itemsPerPage.value + 1,
);
const endIndex = computed(() =>
  Math.min(currentPage.value * itemsPerPage.value, totalItems.value),
);

const visiblePages = computed(() => {
  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages.value, start + maxVisible - 1);

  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  return pages;
});

// Validation du formulaire d'édition de produit
const isProductFormValid = computed(() => {
  if (!productFormData.value || !editingProductId.value) {
    return false;
  }

  // Champs obligatoires de base
  const requiredFields = [
    productFormData.value.reference,
    productFormData.value.idCategorie,
    productFormData.value.idCollection,
    productFormData.value.idProvenance,
    productFormData.value.idDeclinaison,
    productFormData.value.prix,
    productFormData.value.poids, // Poids obligatoire
  ];

  // Pack (quantité du paquet) : obligatoire SEULEMENT si vendu_par !== "tailles"
  if (productFormData.value.venduPar !== "tailles") {
    requiredFields.push(productFormData.value.idPack);
  }

  // Au moins une couleur doit être ajoutée
  const couleursValid =
    productFormData.value.selectedCouleurs &&
    productFormData.value.selectedCouleurs.length > 0;

  // Validation spécifique pour les couleurs mélangées : vérifier que les quantités par taille sont renseignées
  let quantitesMelangeesValid = true;
  if (productFormData.value.venduPar === "melangees") {
    if (
      !productFormData.value.selectedCouleurs ||
      productFormData.value.selectedCouleurs.length === 0
    ) {
      quantitesMelangeesValid = false;
    } else {
      // Vérifier que toutes les couleurs ont des quantités renseignées
      for (const couleur of productFormData.value.selectedCouleurs) {
        const couleurWithQuantites = couleur as any;
        if (
          !couleurWithQuantites.quantitesParTaille ||
          !Array.isArray(couleurWithQuantites.quantitesParTaille)
        ) {
          quantitesMelangeesValid = false;
          break;
        }
        // Vérifier qu'au moins une quantité est > 0
        const hasQuantites = couleurWithQuantites.quantitesParTaille.some(
          (q: number) => q > 0,
        );
        if (!hasQuantites) {
          quantitesMelangeesValid = false;
          break;
        }
      }

      // Validation: vérifier que la somme des quantités des couleurs = quantités du pack
      if (quantitesMelangeesValid && productFormData.value.idPack) {
        const selectedPack = packs.value.find(
          (p: any) =>
            p.id.toString() === productFormData.value.idPack.toString(),
        );

        if (selectedPack && (selectedPack as any).value) {
          const packQuantites = (selectedPack as any).value
            .split(",")
            .map((q: string) => parseInt(q.trim(), 10))
            .filter((q: number) => !isNaN(q));

          const sommeQuantites: number[] = new Array(packQuantites.length).fill(
            0,
          );

          for (const couleur of productFormData.value.selectedCouleurs) {
            const couleurWithQuantites = couleur as any;
            if (couleurWithQuantites.quantitesParTaille) {
              couleurWithQuantites.quantitesParTaille.forEach(
                (qte: number, index: number) => {
                  if (index < sommeQuantites.length) {
                    sommeQuantites[index] += qte || 0;
                  }
                },
              );
            }
          }

          // Comparer avec les quantités du pack
          for (let i = 0; i < packQuantites.length; i++) {
            if (packQuantites[i] !== sommeQuantites[i]) {
              quantitesMelangeesValid = false;
              break;
            }
          }
        }
      }
    }
  }

  // Au moins une composition doit être ajoutée
  const compositionsValid =
    productFormData.value.compositions &&
    productFormData.value.compositions.length > 0;

  // Marque : obligatoire seulement si plusieurs marques disponibles
  if (marques.value.length > 1 && !productFormData.value.idVendeurMarque) {
    return false;
  }

  if (
    isProductShoe.value &&
    String(productFormData.value.dimensions || "").trim() &&
    !isValidProductDimensions(productFormData.value.dimensions)
  ) {
    return false;
  }

  // Vérifier que tous les champs obligatoires sont remplis
  const allRequiredFilled = requiredFields.every(
    (field) =>
      field !== null && field !== undefined && field.toString().trim() !== "",
  );

  const poidsValid = isMelUnitWeightAllowed(
    Number(String(productFormData.value.poids ?? "").replace(",", ".")),
  );

  return (
    allRequiredFilled &&
    poidsValid &&
    couleursValid &&
    compositionsValid &&
    quantitesMelangeesValid
  );
});

// Message d'erreur pour les quantités mélangées
const melangeesQuantitesError = computed((): string => {
  if (productFormData.value.venduPar !== "melangees") return "";
  if (
    !productFormData.value.selectedCouleurs ||
    productFormData.value.selectedCouleurs.length === 0
  )
    return "";
  if (!productFormData.value.idPack) return "";

  const selectedPack = packs.value.find(
    (p: any) => p.id.toString() === productFormData.value.idPack.toString(),
  );
  if (!selectedPack || !(selectedPack as any).value) return "";

  const packQuantites = (selectedPack as any).value
    .split(",")
    .map((q: string) => parseInt(q.trim(), 10))
    .filter((q: number) => !isNaN(q));

  const sommeQuantites: number[] = new Array(packQuantites.length).fill(0);
  for (const couleur of productFormData.value.selectedCouleurs) {
    const c = couleur as any;
    if (c.quantitesParTaille && Array.isArray(c.quantitesParTaille)) {
      c.quantitesParTaille.forEach((qte: number, index: number) => {
        if (index < sommeQuantites.length) {
          sommeQuantites[index] += qte || 0;
        }
      });
    }
  }

  for (let i = 0; i < packQuantites.length; i++) {
    if (packQuantites[i] !== sommeQuantites[i]) {
      return `La somme des quantités des couleurs (${sommeQuantites.join(
        "-",
      )}) ne correspond pas aux quantités du paquet (${packQuantites.join(
        "-",
      )})`;
    }
  }
  return "";
});

// Fonction helper pour afficher le pack avec valeur dans le modal
const getPackDisplayLabel = (produit: any): string => {
  if (!produit.pack) return "-";
  // Utiliser pack_value du backend si disponible (évite de dépendre du chargement des packs)
  if (produit.pack_value) {
    return `${produit.pack} (${produit.pack_value})`;
  }
  if (!produit.id_pack) return produit.pack;
  const pack = packs.value.find((p) => p.id === produit.id_pack);
  if (pack && pack.value) {
    return `${produit.pack} (${pack.value})`;
  }
  return produit.pack;
};

// Fonction helper pour déterminer le type MEL
const getMelType = (mel: string | number): string => {
  const melTypes: Record<string, string> = {
    "0": "eFashion",
    "1": "UPGR",
    "2": "TGR",
  };
  return melTypes[mel.toString()] || "eFashion";
};

// Fonction helper pour déterminer le type de prestation
const getPrestationType = (prestationValeur: number | null): string => {
  const prestationTypes: Record<number, string> = {
    0: "Cintre",
    3: "Uni : Exclusif",
    4: "Couleurs : Exclusif",
    5: "Uni : Libre Droit",
    6: "Couleurs : Libre Droit",
  };

  // Si null, retourne 'Cintre' par défaut (comme ancien système ligne 25: $type=isset($presta[1])?$presta[1]:0)
  return prestationTypes[prestationValeur ?? 0] || "Cintre";
};

const loadDemandes = async () => {
  try {
    isLoading.value = true;
    // Pagination backend + filtrage backend sur TOUS les shootings
    const response = await getMyShootings(
      currentPage.value,
      itemsPerPage.value,
      filters.idShooting || "",
      filters.reference || "",
    );

    if (response.success && response.shootings) {
      totalItems.value = response.total || 0;
      totalPages.value = response.totalPages || 0;

      // Ne pas filtrer totalPieces === 0 : une demande auto-créée peut
      // avoir des produits et des photos alors que la colonne est restée à 0.
      demandes.value = response.shootings.map((shooting: any) => ({
        id: shooting.id,
        mel: shooting.mel,
        melType: getMelType(shooting.mel),
        prestationType: getPrestationType(shooting.prestationValeur),
        prestationValeur: shooting.prestationValeur,
        typeShooting: getMelType(shooting.mel), // Pour le tri seulement
        dateDemande: shooting.dateDemande
          ? new Date(shooting.dateDemande).toLocaleDateString("fr-FR") +
            " " +
            new Date(shooting.dateDemande).toLocaleTimeString("fr-FR", {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "-",
        categories: Array.isArray(shooting.categories)
          ? shooting.categories.join(", ")
          : shooting.categories || "Non spécifié",
        references: Array.isArray(shooting.references)
          ? shooting.references
          : shooting.references
          ? shooting.references.split(",")
          : [],
        dateConfirmation: shooting.dateConfirmation
          ? new Date(shooting.dateConfirmation).toLocaleDateString("fr-FR")
          : "-",
        dateShooting: shooting.dateShooting
          ? new Date(shooting.dateShooting).toLocaleDateString("fr-FR")
          : "-",
        hasShootingDate: !!shooting.dateShooting,
        dateFinalisation: shooting.dateFinalisation || null,
        totalPieces: shooting.totalPieces || 0,
        confirmation: shooting.confirmation,
        finalisation: shooting.finalisation,
        pickup: shooting.pickup,
        status:
          shooting.finalisation === "1" || shooting.finalisation === 1
            ? "finalized"
            : shooting.confirmation === "1"
            ? "confirmed"
            : "pending",
        isColorShooting:
          shooting.prestationValeur === 4 || shooting.prestationValeur === 6,
        isProgrammed: shooting.isProgrammed || false,
        isConfirmed:
          shooting.confirmation === "1" ||
          shooting.confirmation === 1 ||
          !!shooting.dateConfirmation,
        allProductsHavePhotos: shooting.allProductsHavePhotos === true,
      }));
    } else {
      demandes.value = [];
      totalItems.value = 0;
      totalPages.value = 0;
    }
  } catch (error) {
    demandes.value = [];
    totalItems.value = 0;
    totalPages.value = 0;
  } finally {
    isLoading.value = false;
  }
};

// Fonction pour gérer le filtrage (remet à la page 1 et recharge)
const handleFilter = (filterValues: MelFilters) => {
  // Mettre à jour filters avec les valeurs reçues
  Object.assign(filters, filterValues);
  currentPage.value = 1;
  loadDemandes();
};

const resetPickupLabelConfiguration = () => {
  pickupPackageTypes.forEach((type) => {
    selectedPickupPackages[type.id] = false;
    pickupPackageQuantities[type.id] = 1;
  });
  pickupHangerTypeId.value = null;
};

const openPickupLabelModal = async (shootingId: number) => {
  pickupLabelShootingId.value = shootingId;
  resetPickupLabelConfiguration();
  pickupLabelModalVisible.value = true;
  isLoadingPickupLabelConfiguration.value = true;

  try {
    const response = await getShootingPickupLabelConfiguration(shootingId);
    (response.receptions || []).forEach(
      (reception: { typeId: number; quantity: number | null }) => {
        if (reception.typeId >= 1 && reception.typeId <= 4) {
          selectedPickupPackages[reception.typeId] = true;
          pickupPackageQuantities[reception.typeId] = reception.quantity || 1;
        } else if (reception.typeId === 5 || reception.typeId === 6) {
          pickupHangerTypeId.value = reception.typeId;
        }
      },
    );
  } catch (error: any) {
    showError(error?.message || "Impossible de charger les informations de réception.");
  } finally {
    isLoadingPickupLabelConfiguration.value = false;
  }
};

const saveAndOpenPickupLabels = async () => {
  const shootingId = pickupLabelShootingId.value;
  if (!shootingId || pickupHangerTypeId.value === null) return;

  const packages = pickupPackageTypes
    .filter((type) => selectedPickupPackages[type.id])
    .map((type) => ({
      typeId: type.id,
      quantity: Number(pickupPackageQuantities[type.id]),
    }));
  if (
    packages.length === 0 ||
    packages.some(
      (item) =>
        !Number.isInteger(item.quantity) ||
        item.quantity < 1 ||
        item.quantity > 20,
    )
  ) {
    showError("Indiquez une quantité comprise entre 1 et 20 pour chaque contenant.");
    return;
  }

  const labelWindow = window.open("", "_blank");
  try {
    isSavingPickupLabelConfiguration.value = true;
    const response = await saveShootingPickupLabelConfiguration(shootingId, {
      packages,
      hangerTypeId: pickupHangerTypeId.value,
    });
    pickupLabelModalVisible.value = false;
    const labelUrl = getShootingPickupLabelUrl(shootingId);
    if (labelWindow) {
      labelWindow.location.href = labelUrl;
    } else {
      window.open(labelUrl, "_blank", "noopener");
    }
    success(
      `${response.labelCount} étiquette${response.labelCount > 1 ? "s" : ""} générée${response.labelCount > 1 ? "s" : ""}.`,
    );
  } catch (error: any) {
    labelWindow?.close();
    showError(error?.message || "Impossible de générer les étiquettes.");
  } finally {
    isSavingPickupLabelConfiguration.value = false;
  }
};

// Handlers pour la pagination
const handlePageChange = (page: number) => {
  currentPage.value = page;
};

const handleItemsPerPageChange = (newItemsPerPage: number) => {
  itemsPerPage.value = newItemsPerPage;
  currentPage.value = 1;
  loadDemandes();
};

// Watcher pour recharger les données quand on change de page
watch(currentPage, () => {
  loadDemandes();
});

// Watcher pour recharger les caractéristiques quand la catégorie change (sous-sous-catégorie finale)
// Validation automatique du prix réduit (doit être > 0 et < prix)
watch(
  () => [productFormData.value.prixReduit, productFormData.value.prix],
  ([prixReduit, prix]) => {
    if (!prixReduit || prixReduit === null || prixReduit === "") {
      return;
    }

    const prixReduitNum =
      typeof prixReduit === "string" ? parseFloat(prixReduit) : prixReduit;
    const prixNum = typeof prix === "string" ? parseFloat(prix) : prix;

    if (isNaN(prixReduitNum)) {
      productFormData.value.prixReduit = null;
      return;
    }

    // Si le prix est valide, vérifier que le prix réduit est < prix
    if (!isNaN(prixNum) && prixNum > 0) {
      if (prixReduitNum <= 0) {
        // Si prix réduit <= 0, corriger à 0.01
        productFormData.value.prixReduit = "0.01";
      } else if (prixReduitNum >= prixNum) {
        // Si prix réduit >= prix, bloquer en mettant le maximum autorisé (prix - 0.01)
        const prixReduitMax = Math.max(0.01, prixNum - 0.01);
        productFormData.value.prixReduit = prixReduitMax.toFixed(2);
      }
    }
  },
);

// Watcher pour initialiser les quantités par taille quand on passe en mode mélangées
watch(
  () => [productFormData.value.venduPar, productFormData.value.idDeclinaison],
  ([newVenduPar, newIdDeclinaison], [oldVenduPar]) => {
    if (
      newVenduPar === "melangees" &&
      taillesListForMelangeesEdit.value.length > 0
    ) {
      // Initialiser les quantités par taille pour les couleurs existantes
      if (
        productFormData.value.selectedCouleurs &&
        Array.isArray(productFormData.value.selectedCouleurs)
      ) {
        productFormData.value.selectedCouleurs =
          productFormData.value.selectedCouleurs.map((c: any) => {
            if (
              !c.quantitesParTaille ||
              c.quantitesParTaille.length !==
                taillesListForMelangeesEdit.value.length
            ) {
              return {
                ...c,
                quantitesParTaille: new Array(
                  taillesListForMelangeesEdit.value.length,
                ).fill(0),
              };
            }
            return c;
          });
      }
    } else if (oldVenduPar === "melangees" && newVenduPar !== "melangees") {
      // Supprimer les quantités par taille si on quitte le mode mélangées
      if (
        productFormData.value.selectedCouleurs &&
        Array.isArray(productFormData.value.selectedCouleurs)
      ) {
        productFormData.value.selectedCouleurs =
          productFormData.value.selectedCouleurs.map((c: any) => {
            const { quantitesParTaille, ...rest } = c;
            return rest;
          });
      }
    }
  },
);

watch(
  () => productFormData.value.idCategorie,
  async (newCategorie, oldCategorie) => {
    // Ne recharger que si la catégorie change réellement (pas au chargement initial)
    if (newCategorie && editingProductId.value && oldCategorie !== undefined) {
      try {
        const caracResponse = await getCaracteristiques(
          newCategorie.toString(),
        );
        caracteristiques.value = caracResponse.caracteristiques || [];
        // Réinitialiser les caractéristiques sélectionnées car la catégorie a changé
        productFormData.value.selectedCaracteristiques = [];
      } catch (err) {
        caracteristiques.value = [];
      }
    }
  },
);

// Fonction pour vérifier si le vendeur peut télécharger les photos

const canDownloadPhotos = (row: any): boolean => {
  // Libre Droit (5 ou 6) : les photos déjà présentes sont téléchargeables
  // avant la finalisation. Les prestations exclusives Efashion restent bloquées.
  return row.prestationValeur === 5 || row.prestationValeur === 6;
};

// Id du shooting en cours de finalisation (pour désactiver les boutons)
const finalisingShootingId = ref<number | null>(null);

// Confirmation de date de shooting (admin)
const shootingConfirmationVisible = ref(false);
const shootingConfirmationId = ref<number | null>(null);
const shootingConfirmationDate = ref("");
const confirmingShootingId = ref<number | null>(null);

const openConfirmerModal = (id: number) => {
  shootingConfirmationId.value = id;
  shootingConfirmationDate.value = todayDate;
  shootingConfirmationVisible.value = true;
};

const confirmShootingDate = async () => {
  const id = shootingConfirmationId.value;
  if (id === null || !shootingConfirmationDate.value) return;
  if (shootingConfirmationDate.value < todayDate) {
    showError("La date de shooting ne peut pas être antérieure à aujourd'hui.");
    return;
  }

  try {
    confirmingShootingId.value = id;
    await confirmerShooting(id, shootingConfirmationDate.value);
    shootingConfirmationVisible.value = false;
    shootingConfirmationId.value = null;
    success("Shooting confirmé.");
    await loadDemandes();
  } catch (err: any) {
    showError(err?.message || "Erreur lors de la confirmation du shooting");
  } finally {
    confirmingShootingId.value = null;
  }
};

// Bouton « Confirmer » : shooting pas encore confirmé
const canConfirmerShooting = (row: any): boolean => {
  return String(row.mel) !== "1" && !row.isConfirmed;
};

// Le bouton "Finaliser" s'affiche si confirmé et pas encore finalisé (finalisation 0 ou 2)
const canFinaliserShooting = (row: any): boolean => {
  if (String(row.mel) !== "1" && !row.isConfirmed) return false;
  const f = String(row.finalisation ?? "0");
  return f === "0" || f === "2";
};

// Le bouton "Annuler la finalisation" s'affiche dès que le shooting est finalisé (finalisation != 0)
const canAnnulerFinalisation = (row: any): boolean => {
  const f = String(row.finalisation ?? "0");
  return f !== "0";
};

// Modal de confirmation pour l'annulation de la finalisation
const cancelFinalisationVisible = ref(false);
const cancelFinalisationId = ref<number | null>(null);

// Modal de confirmation pour la finalisation
const finalisationVisible = ref(false);
const finalisationId = ref<number | null>(null);

// Finalise (1) ou annule la finalisation (0) d'un shooting — réservé aux admins
const handleFinaliser = async (id: number, finalisation: "0" | "1") => {
  if (finalisingShootingId.value !== null) return;
  if (finalisation === "1") {
    const row = demandes.value.find((d) => Number(d.id) === id);
    if (row && String(row.mel) !== "1" && !row.isConfirmed) {
      showError(
        "Impossible de finaliser : le shooting doit d'abord être confirmé.",
      );
      return;
    }
    if (row && !row.allProductsHavePhotos) {
      showError(
        "Impossible de finaliser : tous les produits doivent avoir au moins une photo.",
      );
      return;
    }
  }
  // Pour l'annulation de la finalisation, on demande confirmation via notre modal
  if (finalisation === "0") {
    cancelFinalisationId.value = id;
    cancelFinalisationVisible.value = true;
    return;
  }

  // Pour la finalisation, on demande aussi confirmation via notre modal
  finalisationId.value = id;
  finalisationVisible.value = true;
};

// Confirme la finalisation depuis la modal
const confirmFinalisation = async () => {
  const id = finalisationId.value;
  finalisationVisible.value = false;
  finalisationId.value = null;
  if (id === null) return;
  await doFinaliser(id, "1");
};

// Confirme l'annulation de la finalisation depuis la modal
const confirmCancelFinalisation = async () => {
  const id = cancelFinalisationId.value;
  cancelFinalisationVisible.value = false;
  cancelFinalisationId.value = null;
  if (id === null) return;
  await doFinaliser(id, "0");
};

// Exécute réellement la (dé)finalisation
const doFinaliser = async (id: number, finalisation: "0" | "1") => {
  if (finalisingShootingId.value !== null) return;

  try {
    finalisingShootingId.value = id;
    const res = await finaliserShooting(id, finalisation);

    if (finalisation === "1") {
      if (res?.facture?.quantite) {
        success(
          `Shooting finalisé : ${res.facture.quantite} référence(s) ajoutée(s) à la facture.`,
        );
      } else {
        success("Shooting finalisé.");
      }
    } else {
      success("Finalisation annulée.");
    }

    await loadDemandes();
  } catch (err: any) {
    showError(err?.message || "Erreur lors de la finalisation du shooting");
  } finally {
    finalisingShootingId.value = null;
  }
};

// Lecture seule si shooting programmé/confirmé — les admins (impersonation) peuvent toujours modifier
const isShootingReadOnly = computed(() => {
  if (authStore.isImpersonating) return false;
  const s = editingShootingData.value?.shooting;
  if (!s) return false;
  return !!(
    s.isProgrammed ||
    s.isConfirmed ||
    s.confirmation === "1" ||
    s.confirmation === 1 ||
    s.dateConfirmation
  );
});

// Fonctions globales pour les boutons d'action
const handleEdit = async (id: number) => {
  try {
    isLoadingEditData.value = true;

    // Réinitialiser l'édition de produit
    editingProductId.value = null;
    productFormData.value = {};

    const response = await getShootingForEdit(id);

    if (response.success && response.shooting) {
      editingShootingData.value = response;
      showEditModal.value = true;
    }
  } catch (err: any) {
    const message = err.message || "Erreur lors du chargement du shooting";
    if (message.includes("programmé")) {
      showError("Ce shooting est déjà programmé et ne peut plus être modifié.");
    } else if (message.includes("confirmé")) {
      showError("Ce shooting est déjà confirmé et ne peut plus être modifié.");
    } else {
      showError(message);
    }
  } finally {
    isLoadingEditData.value = false;
  }
};

const handleEditProduct = async (produit: any) => {
  const shooting = editingShootingData.value?.shooting;
  const isConfirmed =
    shooting?.confirmation === "1" ||
    shooting?.confirmation === 1 ||
    !!shooting?.dateConfirmation;
  if (!authStore.isImpersonating && shooting?.isProgrammed) {
    showError("Ce shooting est déjà programmé et ne peut plus être modifié.");
    return;
  }
  if (!authStore.isImpersonating && isConfirmed) {
    showError("Ce shooting est déjà confirmé et ne peut plus être modifié.");
    return;
  }

  try {
    editingProductId.value = produit.id_produit;
    isLoadingProductData.value = true;

    // Charger les données de référence si pas encore chargées
    if (marques.value.length === 0) {
      const data = await getReferenceData();

      // Charger toutes les données
      marques.value = data.marques;
      collections.value = data.collections;
      provenances.value = data.provenances;
      declinaisons.value = data.declinaisons;
      packs.value = data.packs;
      compositions.value = data.compositions;
      categoriesGenerales.value = data.categories;
      categoriesAll.value = [
        ...(data.categories || []),
        ...(data.sousCategories || []),
        ...(data.sousSousCategories || []),
      ];

      // Charger l'arbre des catégories (structure identique à ProductSheetModal)
      try {
        categoriesTree.value = await getCategoriesTree("fr");
      } catch (treeErr) {
        console.warn("Impossible de charger l'arbre des catégories:", treeErr);
      }

      // Catalogue officiel pour la modal couleurs MEL
      try {
        couleurs.value = await getCouleursOfficielles();
      } catch (err) {
        couleurs.value = [];
      }
    }

    // Charger les caractéristiques pour la catégorie du produit
    if (produit.id_categorie) {
      try {
        const caracResponse = await getCaracteristiques(
          produit.id_categorie.toString(),
        );
        caracteristiques.value = caracResponse.caracteristiques || [];
      } catch (err) {
        console.error("Erreur chargement caractéristiques:", err);
        caracteristiques.value = [];
      }
    }

    // Dédupliquer les IDs
    const uniqueCaracteristiquesIds = produit.caracteristiques_ids
      ? [...new Set(produit.caracteristiques_ids)]
      : [];

    // Convertir les couleurs au format de CouleursSelector : { id, nom, isMain, quantitesParTaille }
    const couleursForSelector =
      produit.couleurs &&
      Array.isArray(produit.couleurs) &&
      produit.couleurs_ids &&
      Array.isArray(produit.couleurs_ids)
        ? produit.couleurs
            .map((couleurNom: string, index: number) => {
              const couleurId = produit.couleurs_ids[index];
              // Chercher le hexColor dans la liste des couleurs du vendeur
              const couleurRef = couleurs.value.find(
                (c: any) => c.id_couleur === parseInt(String(couleurId)),
              );
              const couleurItem: any = {
                id: parseInt(String(couleurId)), // Toujours convertir en nombre
                nom: couleurNom,
                isMain: index === 0,
                hexColor: couleurRef?.hexColor || null,
              };

              // Pour les couleurs mélangées, charger les quantités par taille depuis description_paquet
              if (
                produit.vendu_par === "melangees" &&
                produit.couleurs_descriptions &&
                Array.isArray(produit.couleurs_descriptions)
              ) {
                const description = produit.couleurs_descriptions[index];
                if (description && typeof description === "string") {
                  // Convertir "1-2-1" en [1, 2, 1]
                  couleurItem.quantitesParTaille = description
                    .split("-")
                    .map((q: string) => {
                      const num = parseInt(q.trim());
                      return isNaN(num) ? 0 : num;
                    });
                }
              }

              // Pour couleurs/tailles, mapper stock et tailleStocks depuis couleurs_stocks
              if (
                (produit.vendu_par === "couleurs" ||
                  produit.vendu_par === "tailles") &&
                produit.couleurs_stocks &&
                Array.isArray(produit.couleurs_stocks)
              ) {
                const stockInfo = produit.couleurs_stocks.find(
                  (s: any) =>
                    parseInt(s.id_couleur) === parseInt(String(couleurId)),
                );
                if (stockInfo) {
                  if (stockInfo.stock != null) {
                    couleurItem.stock = stockInfo.stock;
                  }
                  if (
                    Array.isArray(stockInfo.tailleStocks) &&
                    stockInfo.tailleStocks.length > 0
                  ) {
                    couleurItem.tailleStocks = stockInfo.tailleStocks.map(
                      (ts: any) => ({ ...ts }),
                    );
                  }
                }
              }

              return couleurItem;
            })
            .filter((c: any) => !isNaN(c.id)) // Filtrer les IDs invalides
        : [];

    // Initialiser le formulaire avec les données du produit
    productFormData.value = {
      reference: produit.reference_base || produit.reference || "",
      idVendeurMarque: produit.id_vendeur_marque || null,
      poids: produit.poids || null,
      idCategorie: produit.id_categorie || null,
      idCategorieGenerale: null,
      idSousCategorie: null,
      sousCategories: [],
      sousSousCategories: [],
      venduPar: produit.vendu_par || "couleurs",
      idCollection: produit.id_collection || null,
      idProvenance: produit.id_provenance || null,
      idDeclinaison: produit.id_declinaison || null,
      idPack: produit.id_pack || null,
      stock: produit.stock || null,
      prix: produit.prix || null,
      prixReduit: produit.prix_reduit || produit.prixReduit || null,
      dateRemise: produit.dateRemise
        ? new Date(produit.dateRemise).toISOString().split("T")[0]
        : null,
      pourcentageRemise: produit.pourcentageRemise || null,
      dimensions: produit.dimension || null,
      qteMini: produit.qteMini || null,
      selectedCouleurs: couleursForSelector,
      selectedCaracteristiques: uniqueCaracteristiquesIds || [],
      descriptionFr: (produit.description_fr || "").replace(/<br\s*\/?>/gi, "\n"),
      descriptionEn: (produit.description_en || "").replace(/<br\s*\/?>/gi, "\n"),
      descriptionIt: (produit.description_it || "").replace(/<br\s*\/?>/gi, "\n"),
      descriptionEs: (produit.description_es || "").replace(/<br\s*\/?>/gi, "\n"),
      descriptionDe: (produit.description_de || "").replace(/<br\s*\/?>/gi, "\n"),
      descriptionZh: (produit.description_zh || "").replace(/<br\s*\/?>/gi, "\n"),
      // Champs pour CompositionForm
      compositions: [],
      compositionType:
        produit.id_categorie && produit.id_categorie.toString().startsWith("14")
          ? "chaussures"
          : "autres",
      localisations: [],
      compositionsList: [],
    };

    // Charger les localisations et compositions selon le type
    if (productFormData.value.compositionType === "autres") {
      productFormData.value.localisations = compositions.value
        .filter(
          (comp) => comp.id_categorie === null && comp.type === "localisation",
        )
        .map((comp) => ({
          id_composition_localisation: comp.id,
          libelle: comp.libelle,
        }));
      productFormData.value.compositionsList = compositions.value
        .filter(
          (comp) => comp.id_categorie === null && comp.type === "composition",
        )
        .map((comp) => ({
          id_composition: parseInt(comp.id),
          libelle: comp.libelle,
        }));
    } else {
      productFormData.value.localisations = compositions.value
        .filter(
          (comp) => comp.id_categorie === "14" && comp.type === "localisation",
        )
        .map((comp) => ({
          id_composition_localisation: comp.id,
          libelle: comp.libelle,
        }));
      productFormData.value.compositionsList = compositions.value
        .filter(
          (comp) => comp.id_categorie === "14" && comp.type === "composition",
        )
        .map((comp) => ({
          id_composition: parseInt(comp.id),
          libelle: comp.libelle,
        }));
    }

    // Convertir les compositions du produit au format de CompositionForm
    if (
      produit.compositions_details &&
      Array.isArray(produit.compositions_details)
    ) {
      productFormData.value.compositions = produit.compositions_details.map(
        (comp: any) => ({
          localisationId: comp.localisationId
            ? parseInt(comp.localisationId)
            : 0,
          compositionId: parseInt(comp.id),
          compositionName: comp.libelle || "",
          // Garder vide pour afficher le placeholder "0" si la valeur n'existe pas / est 0
          percentage: comp.percentage ? Number(comp.percentage) : "",
        }),
      );
    }

    // Pré-remplir les catégories si on a id_categorie
    if (produit.id_categorie && categoriesAll.value.length > 0) {
      initializeCategoriesFromProduct(produit.id_categorie);
    }
  } catch (err: any) {
    showError(err.message || "Erreur lors du chargement des données");
    editingProductId.value = null;
  } finally {
    isLoadingProductData.value = false;
  }
};

const handleCancelEditProduct = () => {
  editingProductId.value = null;
  productFormData.value = {};
};

// Fonction pour toggle caractéristique
const toggleProductCaracteristique = (idCaracteristique: number) => {
  if (!productFormData.value.selectedCaracteristiques) {
    productFormData.value.selectedCaracteristiques = [];
  }

  const index =
    productFormData.value.selectedCaracteristiques.indexOf(idCaracteristique);

  if (index > -1) {
    productFormData.value.selectedCaracteristiques.splice(index, 1);
  } else {
    if (productFormData.value.selectedCaracteristiques.length >= 5) {
      showError(
        "Vous ne pouvez sélectionner que 5 caractéristiques maximum par produit.",
      );
      return;
    }
    productFormData.value.selectedCaracteristiques.push(idCaracteristique);
  }
};

// Fonctions pour les composition
const handleCompositionInput = async (
  value: string,
  localisationId: string | null,
) => {
  const term = value.trim();

  if (term.length < 1) {
    showCompositionDropdown.value = false;
    compositionAutocompleteOptions.value = [];
    return;
  }

  try {
    isLoadingCompositionAutocomplete.value = true;

    // Filtrer SEULEMENT les compositions de la MÊME localisation
    const selectedCompositionIds = productFormData.value.compositions
      .filter((comp: any) =>
        localisationId
          ? comp.localisationId === parseInt(localisationId)
          : true,
      )
      .map((comp: any) => comp.compositionId)
      .filter((id: number) => id > 0)
      .join("#");

    // Appeler l'API d'autocomplete
    const options = await getCompositionAutocomplete(
      term,
      selectedCompositionIds,
    );
    compositionAutocompleteOptions.value = options;
    showCompositionDropdown.value = options.length > 0;
  } catch (err) {
    console.error("Erreur autocomplete compositions:", err);
    compositionAutocompleteOptions.value = [];
    showCompositionDropdown.value = false;
  } finally {
    isLoadingCompositionAutocomplete.value = false;
  }
};

const selectComposition = (composition: { id: number; label: string }) => {
  showCompositionDropdown.value = false;
  compositionAutocompleteOptions.value = [];
};

const hideCompositionDropdown = () => {
  setTimeout(() => {
    showCompositionDropdown.value = false;
  }, 200);
};

// Fonctions pour gérer les catégories
const handleCategorieChange = () => {
  // Réinitialiser les sous-catégories
  productFormData.value.idSousCategorie = null;
  productFormData.value.idCategorie = null;
  productFormData.value.sousSousCategories = [];

  if (!productFormData.value.idCategorieGenerale) {
    productFormData.value.sousCategories = [];
    return;
  }

  // Filtrer les catégories de niveau 2 (4 chiffres) qui correspondent à la catégorie sélectionnée
  const idCategGen = parseInt(productFormData.value.idCategorieGenerale);
  productFormData.value.sousCategories = categoriesAll.value.filter(
    (cat: any) => {
      const catId = String(cat.id);
      return catId.length === 4 && catId.startsWith(String(idCategGen));
    },
  );
};

const handleSousCategorieChange = () => {
  // Réinitialiser la sous-sous-catégorie
  productFormData.value.idCategorie = null;

  if (!productFormData.value.idSousCategorie) {
    productFormData.value.sousSousCategories = [];
    return;
  }

  // Filtrer les catégories de niveau 3 (6 chiffres) qui correspondent à la sous-catégorie sélectionnée
  const idSousCat = String(productFormData.value.idSousCategorie);
  productFormData.value.sousSousCategories = categoriesAll.value.filter(
    (cat: any) => {
      const catId = String(cat.id);
      return catId.length === 6 && catId.startsWith(idSousCat);
    },
  );

  // Si pas de sous-sous-catégories, utiliser la sous-catégorie comme catégorie finale
  if (productFormData.value.sousSousCategories.length === 0) {
    productFormData.value.idCategorie = productFormData.value.idSousCategorie;
  }
};

const initializeCategoriesFromProduct = (idCategorie: number) => {
  const catIdStr = String(idCategorie);

  // Déterminer le niveau de la catégorie
  if (catIdStr.length === 6) {
    // C'est une sous-sous-catégorie
    const idSousCat = parseInt(catIdStr.substring(0, 4));
    const idCategGen = parseInt(catIdStr.substring(0, 2));

    productFormData.value.idCategorieGenerale = idCategGen;
    productFormData.value.idSousCategorie = idSousCat;
    productFormData.value.idCategorie = idCategorie;

    // Charger les sous-catégories
    productFormData.value.sousCategories = categoriesAll.value.filter(
      (cat: any) => {
        const catId = String(cat.id);
        return catId.length === 4 && catId.startsWith(String(idCategGen));
      },
    );

    // Charger les sous-sous-catégories
    productFormData.value.sousSousCategories = categoriesAll.value.filter(
      (cat: any) => {
        const catId = String(cat.id);
        return catId.length === 6 && catId.startsWith(String(idSousCat));
      },
    );
  } else if (catIdStr.length === 4) {
    // C'est une sous-catégorie
    const idCategGen = parseInt(catIdStr.substring(0, 2));

    productFormData.value.idCategorieGenerale = idCategGen;
    productFormData.value.idSousCategorie = idCategorie;
    productFormData.value.idCategorie = idCategorie;

    // Charger les sous-catégories
    productFormData.value.sousCategories = categoriesAll.value.filter(
      (cat: any) => {
        const catId = String(cat.id);
        return catId.length === 4 && catId.startsWith(String(idCategGen));
      },
    );

    // Charger les sous-sous-catégories (6 digits) qui commencent par cette sous-catégorie
    productFormData.value.sousSousCategories = categoriesAll.value.filter(
      (cat: any) => {
        const catId = String(cat.id);
        return catId.length === 6 && catId.startsWith(catIdStr);
      },
    );
  } else if (catIdStr.length === 2) {
    // C'est une catégorie générale
    productFormData.value.idCategorieGenerale = idCategorie;
    productFormData.value.idCategorie = idCategorie;

    // Charger les sous-catégories (4 digits) qui commencent par cette catégorie générale
    productFormData.value.sousCategories = categoriesAll.value.filter(
      (cat: any) => {
        const catId = String(cat.id);
        return catId.length === 4 && catId.startsWith(catIdStr);
      },
    );
  }
};

const handleSaveProduct = async () => {
  if (!editingProductId.value) {
    return;
  }

  const shooting = editingShootingData.value?.shooting;
  const isConfirmed =
    shooting?.confirmation === "1" ||
    shooting?.confirmation === 1 ||
    !!shooting?.dateConfirmation;
  if (!authStore.isImpersonating && shooting?.isProgrammed) {
    showError("Ce shooting est déjà programmé et ne peut plus être modifié.");
    return;
  }
  if (!authStore.isImpersonating && isConfirmed) {
    showError("Ce shooting est déjà confirmé et ne peut plus être modifié.");
    return;
  }

  // Règle métier: si une Déclinaison est sélectionnée, un Pack doit être sélectionné
  // sauf lorsque le produit est vendu par « Tailles aux choix »
  if (
    productFormData.value?.idDeclinaison &&
    !productFormData.value?.idPack &&
    productFormData.value?.venduPar !== "tailles"
  ) {
    showError(
      "Veuillez sélectionner un Pack lorsque vous choisissez une Déclinaison.",
    );
    return;
  }

  // Validation des compositions : chaque groupe de localisation doit totaliser 100%
  if (
    productFormData.value.compositionType === "autres" &&
    productFormData.value.compositions &&
    productFormData.value.compositions.length > 0
  ) {
    const localisationGroups: Record<number, number> = {};
    let hasInvalidPercentage = false;

    for (const comp of productFormData.value.compositions) {
      const percentage = Number(comp.percentage) || 0;
      if (percentage <= 0) {
        hasInvalidPercentage = true;
        break;
      }
      if (localisationGroups[comp.localisationId]) {
        localisationGroups[comp.localisationId] += percentage;
      } else {
        localisationGroups[comp.localisationId] = percentage;
      }
    }

    if (
      hasInvalidPercentage ||
      Object.values(localisationGroups).some((total) => total !== 100)
    ) {
      showError(
        "Chaque partie de la composition doit totaliser exactement 100% et aucun élément ne peut être à 0%",
      );
      return;
    }
  }

  try {
    isSavingProduct.value = true;

    // Auto-traduction de secours : si une description a été saisie mais que
    // l'utilisateur n'a pas cliqué sur "AI Traduction" (champs traduits vides),
    // on lance la traduction automatiquement avant l'enregistrement.
    // On accepte aussi le cas où l'utilisateur a saisi le texte directement
    // dans l'un des champs de langue (sans passer par le champ principal).
    {
      let srcText = descriptionInputListMel.value?.trim();
      if (!srcText) {
        srcText = (
          productFormData.value.descriptionFr ||
          productFormData.value.descriptionEn ||
          productFormData.value.descriptionIt ||
          productFormData.value.descriptionEs ||
          productFormData.value.descriptionDe ||
          ""
        ).trim();
      }
      const filledCount = [
        productFormData.value.descriptionFr,
        productFormData.value.descriptionEn,
        productFormData.value.descriptionIt,
        productFormData.value.descriptionEs,
        productFormData.value.descriptionDe,
      ].filter((v) => (v || "").trim()).length;
      if (srcText && filledCount < LANG_MAP_LIST.length) {
        if (containsEmoji(srcText)) {
          showError(
            "La description ne doit pas contenir d'emojis : ils ne s'affichent pas correctement sur la fiche produit et perturbent la traduction automatique.",
          );
          return;
        }
        descriptionInputListMel.value = srcText;
        await autoTranslateListMel();
      }
    }

    // Préparer les données pour l'API
    const payload = {
      reference: productFormData.value.reference,
      idVendeurMarque: productFormData.value.idVendeurMarque,
      poids: productFormData.value.poids,
      idCategorie: productFormData.value.idCategorie || null,
      venduPar: productFormData.value.venduPar,
      idCollection: productFormData.value.idCollection || null,
      idProvenance: productFormData.value.idProvenance || null,
      idDeclinaison: productFormData.value.idDeclinaison || null,
      idPack:
        productFormData.value.venduPar === "tailles"
          ? null
          : productFormData.value.idPack || null,
      prix: productFormData.value.prix,
      prixReduit: productFormData.value.prixReduit || null,
      dimensions: productFormData.value.dimensions || null,
      // Extraire les IDs des couleurs avec leurs quantités par taille (si c'est un tableau d'objets)
      couleurs: (productFormData.value.selectedCouleurs || [])
        .map((c: any) => {
          if (typeof c === "object") {
            const couleurItem: any = {
              id: c.id,
            };
            // Pour les couleurs mélangées, ajouter les quantités par taille
            if (
              productFormData.value.venduPar === "melangees" &&
              c.quantitesParTaille &&
              Array.isArray(c.quantitesParTaille)
            ) {
              couleurItem.quantitesParTaille = c.quantitesParTaille;
              // Format pour description_paquet (ex: "1-2-1" pour les quantités)
              couleurItem.description = c.quantitesParTaille.join("-");
            }
            // Pour couleurs, ajouter le stock par couleur
            if (productFormData.value.venduPar === "couleurs") {
              couleurItem.stock = c.stock != null ? c.stock : null;
            }
            // Pour tailles, ajouter les tailleStocks
            if (
              productFormData.value.venduPar === "tailles" &&
              Array.isArray(c.tailleStocks)
            ) {
              couleurItem.tailleStocks = c.tailleStocks;
            }
            return couleurItem;
          }
          return typeof c === "object" ? c.id : c;
        })
        .filter((item: any) => {
          if (typeof item === "object" && item.id) {
            return !isNaN(parseInt(item.id));
          }
          return item && !isNaN(parseInt(item));
        }),
      // Identifier la couleur principale (seulement pour couleurs/tailles, pas pour melangees)
      couleurPrincipaleId:
        productFormData.value.venduPar !== "melangees"
          ? (productFormData.value.selectedCouleurs || []).find(
              (c: any) => c.isMain,
            )?.id || null
          : null,
      // Envoyer les compositions avec leur format complet (localisationId, compositionId, percentage)
      compositions: (productFormData.value.compositions || []).map(
        (c: any) => ({
          id: c.compositionId,
          localisationId: c.localisationId || null,
          percentage:
            c.percentage === "" || c.percentage == null
              ? null
              : Number(c.percentage),
        }),
      ),
      // Les caractéristiques sont déjà des IDs
      caracteristiques: (
        productFormData.value.selectedCaracteristiques || []
      ).filter((id: any) => id && !isNaN(parseInt(id))),
      descriptionFr: productFormData.value.descriptionFr ? productFormData.value.descriptionFr.replace(/\n/g, "<br/>") : null,
      descriptionEn: productFormData.value.descriptionEn ? productFormData.value.descriptionEn.replace(/\n/g, "<br/>") : null,
      descriptionIt: productFormData.value.descriptionIt ? productFormData.value.descriptionIt.replace(/\n/g, "<br/>") : null,
      descriptionEs: productFormData.value.descriptionEs ? productFormData.value.descriptionEs.replace(/\n/g, "<br/>") : null,
      descriptionDe: productFormData.value.descriptionDe ? productFormData.value.descriptionDe.replace(/\n/g, "<br/>") : null,
      descriptionZh: productFormData.value.descriptionZh ? productFormData.value.descriptionZh.replace(/\n/g, "<br/>") : null,
      // Stock et remises
      stock: productFormData.value.stock || null,
      dateRemise: productFormData.value.dateRemise || null,
      pourcentageRemise: productFormData.value.pourcentageRemise || null,
    };

    // Valider le pourcentage de remise
    if (payload.pourcentageRemise && payload.pourcentageRemise !== "") {
      const pourcentage = parseFloat(payload.pourcentageRemise);
      if (isNaN(pourcentage) || pourcentage < 0 || pourcentage > 100) {
        showError("Le pourcentage de remise doit être entre 0 et 100.");
        return;
      }
    }

    const response = await updateProduct(editingProductId.value, payload);

    if (response.success) {
      success("Produit modifié avec succès");

      // Fermer l'édition AVANT le rechargement pour éviter les conflits
      editingProductId.value = null;
      productFormData.value = {};
      descriptionInputListMel.value = ""; // Vider le champ source de traduction

      // Recharger les données du shooting pour mettre à jour la liste
      if (editingShootingData.value?.shooting?.id) {
        try {
          const updatedResponse = await getShootingForEdit(
            editingShootingData.value.shooting.id,
          );
          if (updatedResponse.success) {
            editingShootingData.value = updatedResponse;
          }
        } catch (err) {
          console.error("Erreur lors du rechargement:", err);
        }

        // Recharger la liste des shootings pour mettre à jour le totalPieces dans la liste principale
        await loadDemandes();
      }
    }
  } catch (err: any) {
    const message = err.message || "Erreur lors de la sauvegarde du produit";
    if (message.includes("programmé")) {
      showError("Ce shooting est déjà programmé et ne peut plus être modifié.");
    } else if (message.includes("confirmé")) {
      showError("Ce shooting est déjà confirmé et ne peut plus être modifié.");
    } else {
      showError(message);
    }
  } finally {
    isSavingProduct.value = false;
  }
};

const handleDeleteProduct = async (idProduit: number, reference: string) => {
  const shooting = editingShootingData.value?.shooting;
  const isConfirmed =
    shooting?.confirmation === "1" ||
    shooting?.confirmation === 1 ||
    !!shooting?.dateConfirmation;
  if (!authStore.isImpersonating && shooting?.isProgrammed) {
    showError("Ce shooting est déjà programmé et ne peut plus être modifié.");
    return;
  }
  if (!authStore.isImpersonating && isConfirmed) {
    showError("Ce shooting est déjà confirmé et ne peut plus être modifié.");
    return;
  }

  if (
    !confirm(
      `Voulez-vous vraiment supprimer la référence "${reference}" ?\n\nToutes les couleurs liées seront également supprimées.`,
    )
  ) {
    return;
  }

  try {
    const response = await deleteProduct(idProduit);

    if (response.success) {
      success("Produit supprimé avec succès");

      // Recharger les données du shooting pour mettre à jour la liste
      if (editingShootingData.value?.shooting?.id) {
        const updatedResponse = await getShootingForEdit(
          editingShootingData.value.shooting.id,
        );
        if (updatedResponse.success) {
          editingShootingData.value = updatedResponse;
        }

        // Recharger la liste des shootings aussi
        await loadDemandes();
      }
    }
  } catch (err: any) {
    const message = err.message || "Erreur lors de la suppression du produit";
    if (message.includes("programmé")) {
      showError("Ce shooting est déjà programmé et ne peut plus être modifié.");
    } else if (message.includes("confirmé")) {
      showError("Ce shooting est déjà confirmé et ne peut plus être modifié.");
    } else {
      showError(message);
    }
  }
};

const handleDuplicateProduct = (productId: number, reference: string) => {
  duplicateProductId.value = productId;
  duplicateOriginalRef.value = reference;
  duplicateNewReference.value = "";
  duplicateConfirmVisible.value = true;
};

const confirmDuplicateProduct = async () => {
  if (!duplicateProductId.value || !duplicateNewReference.value) return;

  isDuplicating.value = true;
  try {
    await duplicateProduitForMel(
      duplicateProductId.value,
      duplicateNewReference.value,
      authStore.user?.id,
    );

    // Recharger les données du shooting pour mettre à jour la liste
    if (editingShootingData.value?.shooting?.id) {
      const updatedResponse = await getShootingForEdit(
        editingShootingData.value.shooting.id,
      );
      if (updatedResponse.success) {
        editingShootingData.value = updatedResponse;
      }
    }

    await loadDemandes();
    success(`Produit dupliqué avec la référence ${duplicateNewReference.value}`);
  } catch (e: any) {
    showError(e.message || "Erreur lors de la duplication du produit");
    console.error(e);
  } finally {
    isDuplicating.value = false;
    duplicateConfirmVisible.value = false;
    duplicateProductId.value = null;
    duplicateNewReference.value = "";
    duplicateOriginalRef.value = "";
  }
};

const openDuplicateFromShooting = async (shootingId: number) => {
  duplicateSelectVisible.value = true;
  duplicateSelectLoading.value = true;
  duplicateSelectProducts.value = [];
  duplicateCheckedIds.value = new Set();
  duplicateNewRefs.value = {};
  duplicatePrefix.value = "";
  duplicateSuffix.value = "";
  duplicateCatGen.value = {};
  duplicateCatSous.value = {};
  duplicateCatLeaf.value = {};
  duplicateGlobalCatGen.value = "";
  duplicateGlobalCatSous.value = "";
  duplicateGlobalCatLeaf.value = "";
  duplicateMarque.value = {};
  duplicateGlobalMarque.value = "";

  try {
    await ensureCategoriesTreeForAdmin();

    if (marques.value.length === 0) {
      const data = await getReferenceData();
      marques.value = data.marques;
    }

    const response = await getShootingForEdit(shootingId);
    if (response.success && Array.isArray(response.produits)) {
      duplicateSelectProducts.value = response.produits;
      // 默认全选
      for (const p of response.produits) {
        duplicateCheckedIds.value.add(p.id_produit);
        duplicateNewRefs.value[p.id_produit] = p.reference_base || p.reference || "";
        const parts = adminParseIdCategorie(p.id_categorie);
        duplicateCatGen.value[p.id_produit] = parts.gen;
        duplicateCatSous.value[p.id_produit] = parts.sous;
        duplicateCatLeaf.value[p.id_produit] = parts.leaf;
        duplicateMarque.value[p.id_produit] = p.id_vendeur_marque || "";
      }
    }
  } catch (e: any) {
    showError(e.message || "Erreur lors du chargement des produits");
    duplicateSelectVisible.value = false;
  } finally {
    duplicateSelectLoading.value = false;
  }
};

const toggleSelectAll = () => {
  if (duplicateCheckedIds.value.size === duplicateSelectProducts.value.length) {
    duplicateCheckedIds.value = new Set();
  } else {
    duplicateCheckedIds.value = new Set(
      duplicateSelectProducts.value.map((p) => p.id_produit)
    );
  }
};

const toggleProductCheck = (id: number) => {
  const newSet = new Set(duplicateCheckedIds.value);
  if (newSet.has(id)) {
    newSet.delete(id);
  } else {
    newSet.add(id);
  }
  duplicateCheckedIds.value = newSet;
};

const applyPrefixToAll = () => {
  for (const p of duplicateSelectProducts.value) {
    if (duplicateCheckedIds.value.has(p.id_produit)) {
      const base = p.reference_base || p.reference || "";
      duplicateNewRefs.value[p.id_produit] = duplicatePrefix.value + base;
    }
  }
};

const applyPrefixSuffixToAll = () => {
  for (const p of duplicateSelectProducts.value) {
    if (duplicateCheckedIds.value.has(p.id_produit)) {
      const base = p.reference_base || p.reference || "";
      duplicateNewRefs.value[p.id_produit] = duplicatePrefix.value + base + duplicateSuffix.value;
    }
  }
};

const onDupCatGenChange = (productId: number) => {
  duplicateCatSous.value[productId] = "";
  duplicateCatLeaf.value[productId] = "";
};

const onDupCatSousChange = (productId: number) => {
  duplicateCatLeaf.value[productId] = "";
};

const onGlobalCatGenChange = () => {
  duplicateGlobalCatSous.value = "";
  duplicateGlobalCatLeaf.value = "";
};

const onGlobalCatSousChange = () => {
  duplicateGlobalCatLeaf.value = "";
};

const getDupFinalCategory = (productId: number): string => {
  const leaf = duplicateCatLeaf.value[productId];
  if (leaf) return leaf;
  const sous = duplicateCatSous.value[productId];
  if (sous) return sous;
  return duplicateCatGen.value[productId] || "";
};

const applyGlobalCategoryToAll = () => {
  for (const p of duplicateSelectProducts.value) {
    if (duplicateCheckedIds.value.has(p.id_produit)) {
      duplicateCatGen.value[p.id_produit] = duplicateGlobalCatGen.value;
      duplicateCatSous.value[p.id_produit] = duplicateGlobalCatSous.value;
      duplicateCatLeaf.value[p.id_produit] = duplicateGlobalCatLeaf.value;
    }
  }
};

const applyGlobalMarqueToAll = () => {
  for (const p of duplicateSelectProducts.value) {
    if (duplicateCheckedIds.value.has(p.id_produit)) {
      duplicateMarque.value[p.id_produit] = duplicateGlobalMarque.value;
    }
  }
};

const canConfirmBatchDuplicate = computed(() => {
  if (duplicateCheckedIds.value.size === 0) return false;
  for (const id of duplicateCheckedIds.value) {
    const newRef = duplicateNewRefs.value[id]?.trim();
    if (!newRef) return false;
    // La nouvelle référence ne doit pas être identique à l'originale
    const orig = duplicateSelectProducts.value.find((p: any) => p.id_produit === id);
    const origRef = (orig?.reference_base || orig?.reference || "").trim();
    if (newRef === origRef) return false;
  }
  return true;
});

const confirmBatchDuplicate = async () => {
  if (!canConfirmBatchDuplicate.value) return;

  isDuplicating.value = true;
  let successCount = 0;
  let errorCount = 0;
  let sharedShootingId: number | undefined = undefined;

  try {
    for (const id of duplicateCheckedIds.value) {
      const newRef = duplicateNewRefs.value[id]?.trim();
      if (!newRef) continue;
      try {
        const newProduct = await duplicateProduitForMel(id, newRef, authStore.user?.id, sharedShootingId);

        // Après la première duplication, récupérer le shooting créé pour le partager
        if (!sharedShootingId && newProduct?.id_shooting) {
          sharedShootingId = newProduct.id_shooting;
        }

        // Si la catégorie a changé, mettre à jour le nouveau produit
        const finalCat = getDupFinalCategory(id);
        const origProduct = duplicateSelectProducts.value.find((p: any) => p.id_produit === id);
        const origCat = origProduct?.id_categorie ? String(origProduct.id_categorie) : "";
        const finalMarque = duplicateMarque.value[id] || null;
        const origMarque = origProduct?.id_vendeur_marque || null;
        const payload: any = {};
        if (finalCat && finalCat !== origCat) {
          payload.idCategorie = parseInt(finalCat, 10);
        }
        if (finalMarque && Number(finalMarque) !== Number(origMarque)) {
          payload.idVendeurMarque = finalMarque;
        }
        if (Object.keys(payload).length && newProduct?.id_produit) {
          try {
            await updateProduct(newProduct.id_produit, payload);
          } catch (catErr: any) {
            console.error(`Erreur mise à jour produit ${newProduct.id_produit}:`, catErr);
          }
        }

        successCount++;
      } catch (e: any) {
        errorCount++;
        console.error(`Erreur duplication produit ${id}:`, e);
      }
    }

    await loadDemandes();

    if (successCount > 0) {
      success(`${successCount} produit(s) dupliqué(s) avec succès`);
    }
    if (errorCount > 0) {
      showError(`${errorCount} produit(s) en erreur lors de la duplication`);
    }
  } finally {
    isDuplicating.value = false;
    duplicateSelectVisible.value = false;
    duplicateCheckedIds.value = new Set();
    duplicateSelectProducts.value = [];
    duplicateNewRefs.value = {};
    duplicatePrefix.value = "";
    duplicateSuffix.value = "";
    duplicateCatGen.value = {};
    duplicateCatSous.value = {};
    duplicateCatLeaf.value = {};
    duplicateGlobalCatGen.value = "";
    duplicateGlobalCatSous.value = "";
    duplicateGlobalCatLeaf.value = "";
    duplicateMarque.value = {};
    duplicateGlobalMarque.value = "";
  }
};

const getEditShootingHref = (id: number) =>
  router.resolve({
    name: "catalog/listmel",
    query: { edit: id.toString() },
  }).href;

const getAdminRefsHref = (id: number) =>
  router.resolve({
    name: "catalog/listmel",
    query: { adminRefs: id.toString() },
  }).href;

const getUploadShootingHref = (id: number) =>
  router.resolve({
    name: "catalog/uploadphoto",
    query: { shootingId: id.toString() },
  }).href;

const handleUpload = (id: number) => {
  router.push({
    name: "catalog/uploadphoto",
    query: { shootingId: id.toString() },
  });
};

const handlePhotos = async (id: number) => {
  try {
    await downloadShootingPhotos(id);
    success("Le fichier ZIP a été téléchargé avec succès.");
  } catch (error) {
    console.error("Erreur lors du téléchargement des photos:", error);
    const errorMessage =
      error instanceof Error ? error.message : "Une erreur est survenue";
    showError(errorMessage);
  }
};

const handleProductPhotos = async (productId: number) => {
  try {
    await downloadProductPhotos(productId);
    success("Le fichier ZIP a été téléchargé avec succès.");
  } catch (error) {
    console.error("Erreur lors du téléchargement des photos:", error);
    const errorMessage =
      error instanceof Error ? error.message : "Une erreur est survenue";
    showError(errorMessage);
  }
};

// Fonction pour obtenir la classe CSS selon le statut de confirmation
const getRowClass = (row: any) => {
  if (row.status === "confirmed" || row.status === "finalized") {
    return "border-l-8 !border-l-green-400";
  }
  return "border-l-8 !border-l-yellow-400";
};

// Ajouter les fonctions globales
const parseQueryId = (raw: unknown): number | null => {
  const value = Array.isArray(raw) ? raw[0] : raw;
  const id = Number(value);
  if (!Number.isFinite(id) || id <= 0) return null;
  return id;
};

const clearQueryParams = async (...keys: string[]) => {
  const nextQuery = { ...route.query };
  let changed = false;
  for (const key of keys) {
    if (key in nextQuery) {
      delete nextQuery[key];
      changed = true;
    }
  }
  if (changed) {
    await router.replace({ name: "catalog/listmel", query: nextQuery });
  }
};

const openEditFromQuery = async () => {
  const id = parseQueryId(route.query.edit);
  if (id == null) return;
  await handleEdit(id);
  await clearQueryParams("edit");
};

const openAdminRefsFromQuery = async () => {
  const id = parseQueryId(route.query.adminRefs);
  if (id == null) return;
  await openAdminRefsFocus(id);
  await clearQueryParams("adminRefs");
};

onMounted(async () => {
  // Rendre les fonctions disponibles globalement
  (window as any).handleEdit = handleEdit;
  (window as any).handleUpload = handleUpload;
  (window as any).handlePhotos = handlePhotos;

  if (!authStore.isInitialized) {
    await authStore.checkAuth();
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: "auth" });
    return;
  }
  loadDemandes();
  await openEditFromQuery();
  await openAdminRefsFromQuery();
});
</script>

<style scoped>
/* Styles pour les bordures colorées des lignes selon le statut */
:deep(.border-l-green-400) {
  border-left-color: #4ade80 !important;
}

:deep(.border-l-yellow-400) {
  border-left-color: #facc15 !important;
}

/* Assurer que les bordures sont visibles */
:deep(.border-l-8) {
  border-left-width: 8px !important;
  border-left-style: solid !important;
}

:deep(table) {
  font-size: 0.813rem; /* 13px */
}

:deep(th) {
  padding: 0.5rem 0.75rem !important;
  white-space: nowrap;
}

:deep(td) {
  padding: 0.5rem 0.75rem !important;
  font-size: 0.813rem;
}

/* Limiter la largeur des colonnes de dates */
:deep(td:nth-child(3)),
:deep(td:nth-child(5)),
:deep(td:nth-child(6)) {
  white-space: nowrap;
  font-size: 0.75rem; /* 12px pour les dates */
}

/* Colonnes Type et Catégories */
:deep(td:nth-child(2)),
:deep(td:nth-child(4)) {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Colonne Actions - Réduire encore plus */
:deep(td:last-child) {
  padding: 0.25rem !important;
}

/* Styles pour la zone d'édition - réduire tous les éléments */
.edit-zone :deep(label) {
  font-size: 0.813rem !important; /* 13px */
  /* margin-bottom: 0.25rem !important; */
}

.edit-zone :deep(input),
.edit-zone :deep(select),
.edit-zone :deep(textarea) {
  font-size: 0.813rem !important; /* 13px */
  padding: 0.375rem 0.625rem !important; /* py-1.5 px-2.5 */
}

.edit-zone :deep(.grid) {
  gap: 0.75rem !important; /* Réduire gap-4 à gap-3 */
}

.edit-zone :deep(.space-y-4 > * + *) {
  margin-top: 0.75rem !important;
}

.edit-zone :deep(.mb-4) {
  margin-bottom: 0.75rem !important;
}

.edit-zone :deep(.mt-4) {
  margin-top: 0.75rem !important;
}

.edit-zone :deep(button) {
  font-size: 0.813rem !important;
  padding: 0.375rem 0.75rem !important;
}

/* Responsive pour la zone d'édition */
@media (max-width: 800px) {
  /* Toutes les grilles passent en 1 colonne */
  .edit-zone :deep(.grid) {
    grid-template-columns: 1fr !important;
    gap: 0.75rem !important;
  }

  /* Inputs plus grands sur mobile */
  .edit-zone :deep(input),
  .edit-zone :deep(select),
  .edit-zone :deep(textarea) {
    font-size: 0.875rem !important; /* 14px */
    padding: 0.5rem 0.75rem !important;
  }

  .edit-zone :deep(.space-y-4 > * + *) {
    margin-top: 0.75rem !important;
  }

  .edit-zone h4 {
    font-size: 0.875rem !important;
  }

  /* Boutons Annuler et Enregistrer en colonne (pleine largeur) */
  .edit-zone .action-buttons {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 0.75rem !important;
  }

  .edit-zone .action-buttons button {
    width: 100% !important;
    justify-content: center !important;
  }
}

.edit-zone :deep(.bg-white.rounded-lg) {
  background-color: #fefce8 !important; /* yellow-50 */
}

@media (min-width: 801px) and (max-width: 1024px) {
  .edit-zone :deep(.md\\:grid-cols-3) {
    grid-template-columns: repeat(
      2,
      minmax(0, 1fr)
    ) !important; /* 2 colonnes sur tablette */
  }
}

/* Styles pour la modal d'édition du shooting */
.modal-edit-zone :deep(label) {
  font-size: 0.813rem !important; /* 13px */
  /* margin-bottom: 0.25rem !important; */
}

.modal-edit-zone :deep(input),
.modal-edit-zone :deep(select),
.modal-edit-zone :deep(textarea) {
  font-size: 0.813rem !important;
  padding: 0.375rem 0.625rem !important;
}

.modal-edit-zone :deep(.space-y-4 > * + *) {
  margin-top: 0.75rem !important;
}

.modal-edit-zone :deep(button) {
  font-size: 0.813rem !important;
  padding: 0.375rem 0.75rem !important;
}

/* Responsive pour la modal */
@media (max-width: 800px) {
  /* Section modèle en colonne */
  .modal-edit-zone .model-section {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 0.75rem !important;
  }

  .modal-edit-zone .model-section select {
    max-width: 100% !important;
    font-size: 0.875rem !important;
    padding: 0.5rem 0.75rem !important;
  }

  .modal-edit-zone .model-section button {
    width: 100% !important;
    justify-content: center !important;
  }

  /* Titre nombre de pièces plus petit */
  .modal-edit-zone h3 {
    font-size: 1rem !important;
  }

  /* Tableau scrollable horizontalement sur mobile */
  .modal-edit-zone table {
    min-width: 700px; /* Force une largeur minimale pour permettre le scroll */
  }

  /* Colonne d'édition ne suit pas min-width du tableau */
  .modal-edit-zone .edit-zone {
    overflow-x: visible !important;
  }
}

/* Styles généraux pour le scroll du tableau modal */
.modal-edit-zone > div {
  -webkit-overflow-scrolling: touch;
}

/* Admin réf. MEL : pas de troncature (les :deep globaux sur td nth-child s’appliquent aussi ici) */
.admin-mel-refs-root :deep(.admin-mel-refs-table td:nth-child(2)),
.admin-mel-refs-root :deep(.admin-mel-refs-table td:nth-child(4)),
.admin-mel-refs-root :deep(.admin-mel-refs-table th:nth-child(2)),
.admin-mel-refs-root :deep(.admin-mel-refs-table th:nth-child(4)) {
  max-width: none !important;
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: normal !important;
}

.admin-mel-refs-root :deep(.admin-mel-refs-table td:nth-child(3)),
.admin-mel-refs-root :deep(.admin-mel-refs-table th:nth-child(3)) {
  white-space: normal !important;
}

.admin-mel-refs-root :deep(.admin-mel-refs-table th) {
  white-space: normal !important;
}
</style>
